import { isSignupSendingBlocked } from '@/components/signup/activation';
import { NoteEditor } from '@/components/mentions/NoteEditor';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import axios from 'axios';
import { TYPING_HEARTBEAT_SECONDS } from '@/actions/typingIndicator';
import {
  ArrowUp,
  BookOpen,
  BriefcaseBusiness,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Clock,
  CreditCard,
  DollarSign,
  Edit,
  FileText,
  Handshake,
  Hash,
  Loader2,
  Mail,
  MessageCircleMore,
  MessageSquare,
  Minimize2,
  NotebookPen,
  Paperclip,
  Pencil,
  Plus,
  Send,
  Smile,
  SpellCheck,
  Video,
  Workflow,
  X,
  Zap,
} from 'lucide-react';

import moment from 'moment';
import { Component, createRef } from 'react';
import { flushSync } from 'react-dom';
import Dropzone from 'react-dropzone';
import { connect } from 'react-redux';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { IconButton } from '@/components/ui/custom/IconButton';
import { Dialog } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { downscaleImage } from '@/utils/downscaleImage';
import { ThemeProviderContext } from '@/components/ui/theme-provider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { touchHitArea } from '@/components/ui/touch-target';
import { ActionSheet, BottomSheet, BottomSheetTextButton } from '@/components/mobile';
import { isTouchViewport } from '@/hooks/use-mobile';
import { clearAiDraft } from '@/slices/aiEmployeeSlice';
import { hasAiEmployeeProduct } from '@/utils/ai-employee-access';
import { fetchUser } from '../../../actions';
import { fetchCustomerRecentInvoices, fetchRecentInvoices } from '../../../actions/payments';
import { getTemplates } from '../../../actions/templates';
import {
  addOptimisticMessage,
  clearComposerRestore,
  removeOptimisticMessage,
} from '../../../actions/webchat';
import getWebchatUrl from '../../../utils/getWebchatUrl';
import isEmpty from '../../../utils/isEmpty';
import AttachFormButton from '../../forms/AttachFormButton';
import AddToSequenceButton from '../../message-sequence/AddToSequenceButton';
import { checkIsImage } from '../../utils/isImage';
import formatAmount from '../../utils/payments/formatAmount';
import TemplateSelector from '../../utils/TemplateSelector';
import AIEnhanceButton from './AIEnhanceButton';
import AiDraftCard from './AiDraftCard';
import {
  aiDraftId,
  dismissDraftFromCard,
  forgetUsedDraft,
  hasAiDraftContent,
  isDraftInComposer,
  putDraftInComposer,
  restoreComposer,
} from './aiDraft';
import TalkButton from '@/components/ui/custom/TalkButton';
import ErrorDialog from './Dialogs/ErrorDialog';
import FacebookMessageTagDialog from './Dialogs/FacebookMessageTagDialog';
import ImagePreview from './ImagePreview';
import LongPressArea from './Chatfeed/LongPressArea';
import {
  getNoteAttachmentUrl,
  isImageAttachment,
  storableNoteAttachments,
  uploadNoteAttachment,
} from './noteAttachments';
import QuickScheduleOptions from './scheduled-messages/QuickScheduleOptions';

const REVIEW_INVITATION_TAG = '((review_invitation))';
const IMAGE_TAG = '((image))';
const DESCRIPTION_TAG = '((description))';

const MAX_ATTACHMENT_BYTES = 19999999; // ~20mb; images are downscaled client-side before upload
const MAX_ATTACHMENTS = 10;
const TOO_MANY_ATTACHMENTS_MESSAGE = `You can only send a maximum of ${MAX_ATTACHMENTS} attachments at once.`;

// Mirrors Messenger.addMedia in chekkit-webchat: only these uploads are sent as
// MMS media. Anything else (PDFs, documents, video) goes out as a link in the text.
const SENT_AS_MEDIA_RE = /webchat-image|review-request-photo|chekkit-template-image/;

const acceptedFileTypes = ['image/gif', 'image/png', 'image/jpeg', 'image/bmp'];
const attachmentAcceptConfig = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt'],
  'audio/mpeg': ['.mp3', '.mpeg'],
  'audio/mp4': ['.m4a'],
  'audio/x-m4a': ['.m4a'],
  'audio/amr': ['.amr'],
  'video/mp4': ['.mp4'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};

// Who can attach a payment request: the toolbar button and the phone "+" sheet.
const PAYMENT_ACCESS_LEVELS = [
  'owner',
  'location-admin',
  'team-leader',
  'team-member',
  'message-leader',
  'contributor',
];

// A phone held sideways with the keyboard up leaves about 110–200px for the
// whole thread, and only `kb-open:` can tell (the layout viewport keeps its
// height). There the desktop composer drops to one row, the field with Send
// beside it, so Send and some of the conversation stay above the keyboard.
// The other tools come back when the keyboard goes down.
const SIDEWAYS_TYPING_HIDE = 'kb-open:max-xl:[@media(max-height:560px)]:hidden';
const SIDEWAYS_TYPING_ROW =
  'kb-open:max-xl:[@media(max-height:560px)]:grid kb-open:max-xl:[@media(max-height:560px)]:grid-cols-[minmax(0,1fr)_auto] kb-open:max-xl:[@media(max-height:560px)]:items-end';
// A phone held sideways is short with the keyboard down too, so attachments
// and a payment request are always chips in a column before the field there
// (renderSidewaysAttachments) instead of full previews stacked above it.
// Keyboard down, the other rows (Reply/Note, the toolbar) span both columns.
const SIDEWAYS_HIDE = 'max-xl:[@media(max-height:560px)]:hidden';
const SIDEWAYS_SHOW = 'hidden max-xl:[@media(max-height:560px)]:flex';
const SIDEWAYS_ROW_WITH_ATTACHMENTS =
  'max-xl:[@media(max-height:560px)]:grid max-xl:[@media(max-height:560px)]:grid-cols-[auto_minmax(0,1fr)] max-xl:[@media(max-height:560px)]:items-center kb-open:max-xl:[@media(max-height:560px)]:grid-cols-[auto_minmax(0,1fr)_auto] kb-open:max-xl:[@media(max-height:560px)]:items-end';
const SIDEWAYS_FULL_ROW =
  'max-xl:[@media(max-height:560px)]:col-span-full kb-open:max-xl:[@media(max-height:560px)]:col-auto';
// Anything still taller than the space the thread gives up (an open payment
// form, an AI draft) scrolls inside the composer; Messages caps its row
// (SIDEWAYS_ROWS).
const SIDEWAYS_SCROLL =
  'max-xl:[@media(max-height:560px)]:min-h-0 max-xl:[@media(max-height:560px)]:overflow-y-auto max-xl:[@media(max-height:560px)]:overscroll-contain';

// What the composer hands to its next mount for the same conversation. The
// inbox swaps layouts when a phone rotates across the md breakpoint, and the
// text already lives in localStorage, but attachments, a payment request and
// Note mode would otherwise be lost.
const HANDOFF_MAX_AGE_MS = 5000;
const HANDOFF_KEYS = [
  'composerMode',
  'attachments',
  'amount',
  'description',
  'reference',
  'showPaymentRequestCompleted',
  'includeConvenienceFee',
  'addPaymentRequestTemplate',
  'language',
];
let composerHandoff = null;

const takeComposerHandoff = (conversationId) => {
  const handoff = composerHandoff;
  composerHandoff = null;
  if (!handoff || !conversationId || handoff.conversationId !== conversationId) return null;
  if (Date.now() - handoff.at > HANDOFF_MAX_AGE_MS) return null;
  return handoff.state;
};

class ChatBox extends Component {
  static contextType = ThemeProviderContext;

  constructor(props) {
    super(props);

    this.state = {
      composerMode: 'reply', // 'reply' (message to customer) | 'note' (internal team note)
      message: '',
      attachments: [], // {loading: Bool, mediaUrl: String?, preview: String}
      amount: '',
      description: '',
      reference: '',
      templatesExpanded: false,
      paymentsExpanded: false,
      showPaymentRequestCompleted: false,
      openErrorDialog: false,
      errorMessage: '',
      errorTitle: '',
      searchedTemplates: null,
      loading: false,
      language: '',
      openFacebookMessageTagDialog: false,
      facebookHumanAgentRetryDraft: null,
      showEmojiPicker: false,
      shouldCloseConversation: false,
      scheduledSendDate: null,
      anchorEl: null,
      dropdownOpen: false,
      includeConvenienceFee: false,
      addPaymentRequestTemplate: false,
      aiIsTyping: false,
      aiTypingText: '',
      activeAiDraftId: null,
      // Phones: the "+" tools sheet, the send options and the sheets they open.
      mobileToolsOpen: false,
      mobileSendOptionsOpen: false,
      mobileFormsOpen: false,
      mobileSequenceOpen: false,
      // The open schedule step: null, 'clock' (toolbar button, or the phone
      // sheet) or 'menu' (send menu). Both layouts read it, so the step and its
      // time survive a rotation across md instead of silently scheduling the next send.
      scheduleOpen: null,
      // Below xl the toolbar's tools can run past its edges; arrows show where.
      toolsScrollLeft: false,
      toolsScrollRight: false,
      // A phone held sideways: attachment chips not fully in view.
      stripHidden: 0,
    };

    this.webchatApiUrl = getWebchatUrl(window.location.href);

    this.dropzoneRef = createRef();
    this.popoverTriggerRef = createRef();
    this.datePickerRef = createRef();

    this.dropdownTriggerRef = createRef();
    this.focusRef = createRef();
    this.aiEnhanceRef = createRef();

    this.emojiEventListener = null;
  }

  componentDidMount() {
    document.addEventListener('focusout', this.handleComposerFocusOut, true);
    this.restoreNoteDraft();
    if (this.props.activeConversation) {
      // Only what this person typed comes back; an AI draft waits on its card.
      this.setState(
        restoreComposer(
          this.composerStorage(),
          this.props.activeConversation._id,
          this.getCurrentAiDraft(),
        ),
      );
      const handoff = takeComposerHandoff(this.props.activeConversation._id);
      if (handoff) this.setState(handoff);
    }

    if (this.props.openPaymentsOnMount) {
      this.togglePayments();
    }

    if (this.chatboxElement) {
      // In the new-conversation composer we want the recipient (To:) field to
      // hold focus, not the message box, so callers can opt out of autofocus.
      // Phones: never raise the keyboard just because a thread opened.
      if (this.props.autoFocusOnMount !== false && !this.props.isMobile) {
        this.chatboxElement.focus();
      }
      this.autoResizeTextarea(this.chatboxElement);
    }
  }

  isComposerField = (elem) =>
    Boolean(
      elem &&
        (elem.id === 'chatBoxTextBox' ||
          (elem.isContentEditable && elem.closest?.('#chatBoxTextBoxAndButtons'))),
    );

  // On a touch screen the browser can drop the focus just before a rotation's
  // re-render (the old layout is hidden first), so remember when it left.
  handleComposerFocusOut = (event) => {
    if (this.isComposerField(event.target) && !this.isComposerField(event.relatedTarget)) {
      this.composerBlurredAt = Date.now();
    }
  };

  // Rotating across md swaps the phone composer for the desktop one (or back).
  // Note whether the person was typing so the new message box takes the caret.
  getSnapshotBeforeUpdate(prevProps) {
    if (Boolean(prevProps.isMobile) === Boolean(this.props.isMobile)) return null;
    const active = document.activeElement;
    const typing =
      this.isComposerField(active) ||
      ((!active || active === document.body) && Date.now() - (this.composerBlurredAt || 0) < 1000);
    return typing ? { refocusComposer: true } : null;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { resolvedTheme: theme } = this.context;
    window.document.documentElement.style.colorScheme = theme;

    if (snapshot?.refocusComposer) {
      if (this.state.composerMode === 'note') {
        // The note editor fills itself in after mounting; its own autoFocus
        // waits for that and puts the caret at the end.
        this.setState({ noteAutoFocus: true });
      } else if (this.chatboxElement) {
        const end = this.chatboxElement.value.length;
        this.chatboxElement.focus({ preventScroll: true });
        this.chatboxElement.setSelectionRange(end, end);
      }
    } else if (prevState.noteAutoFocus && this.state.noteAutoFocus) {
      this.setState({ noteAutoFocus: false });
    }

    if (this.chatboxElement) {
      this.autoResizeTextarea(this.chatboxElement);
    }

    // Consume a pendingComposerRestore payload (set when the user clicks a
    // failed bubble) — pull the text + attachments back into composer state.
    const pending = this.props.pendingComposerRestore;
    const prevPending = prevProps.pendingComposerRestore;
    if (pending?.key && pending.key !== prevPending?.key) {
      const restoredAttachments = Array.isArray(pending.attachmentUrls)
        ? pending.attachmentUrls.map((mediaUrl) => ({
            mediaUrl,
            preview: mediaUrl,
            loading: false,
            path: mediaUrl,
          }))
        : [];
      if (pending.sender === 'note') {
        const draft = {
          text: pending.text || '',
          mentions: pending.mentions || [],
          attachments: pending.attachments || [],
          requestId: pending.requestId || crypto.randomUUID(),
        };
        this.setState({ composerMode: 'note', noteDraft: draft });
        this.saveNoteDraft(draft);
      } else {
        this.setState({ message: pending.text || '', attachments: restoredAttachments });
      }
      const el = document.getElementById('chatBoxTextBox');
      if (el) el.focus();
      this.props.clearComposerRestore?.();
    }

    if (this.props.activeConversation?._id !== prevProps.activeConversation?._id) this.restoreNoteDraft();

    // Check if the _id prop has changed, e.g. new conversation selected
    if (
      this.props.activeConversation &&
      this.props.activeConversation._id !== prevProps.activeConversation._id
    ) {
      // Only what this person typed comes back; an AI draft waits on its card.
      this.setState(
        restoreComposer(
          this.composerStorage(),
          this.props.activeConversation._id,
          this.props.aiDraftByConversation?.[this.props.activeConversation._id],
        ),
      );
      return;
    }

    // A draft that arrives while the conversation is open shows on its card and
    // never replaces the composer, so there is nothing to load here.
    if (hasAiEmployeeProduct(this.props.auth) && this.props.activeConversation) {
      const convId = this.props.activeConversation._id;
      const currentDraft = this.props.aiDraftByConversation?.[convId];
      const prevDraft = prevProps.aiDraftByConversation?.[convId];

      if (!currentDraft && prevDraft) {
        this.setState({ activeAiDraftId: null });
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this._handleKeyDown);
    document.removeEventListener('focusout', this.handleComposerFocusOut, true);
    this.setChatboxElement(null);
    this.setToolsScroller(null);
    this.setStripScroller(null);
    const conversationId = this.props.activeConversation?._id;
    if (conversationId) {
      composerHandoff = {
        conversationId,
        at: Date.now(),
        state: Object.fromEntries(HANDOFF_KEYS.map((key) => [key, this.state[key]])),
      };
    }
  }

  // Short-code conversations render without a textarea, so the paste listener
  // has to follow the element rather than assuming it exists on mount.
  setChatboxElement = (elem) => {
    if (this.chatboxElement === elem) {
      return;
    }
    if (this.chatboxElement) {
      this.chatboxElement.removeEventListener('paste', this._pasteHandler);
    }
    this.chatboxElement = elem;
    if (elem) {
      elem.addEventListener('paste', this._pasteHandler);
      this.autoResizeTextarea(elem);
    }
  };

  // The desktop toolbar's tool row scrolls sideways when it is too narrow
  // (iPads, phones held sideways); track whether there is more either way.
  setToolsScroller = (elem) => {
    if (this.toolsScroller === elem) return;
    this.toolsResizeObserver?.disconnect();
    this.toolsResizeObserver = null;
    this.toolsScroller = elem;
    if (elem && typeof ResizeObserver !== 'undefined') {
      this.toolsResizeObserver = new ResizeObserver(this.updateToolsScroll);
      this.toolsResizeObserver.observe(elem);
      if (elem.firstElementChild) this.toolsResizeObserver.observe(elem.firstElementChild);
    }
  };

  updateToolsScroll = () => {
    const elem = this.toolsScroller;
    if (!elem) return;
    const toolsScrollLeft = elem.scrollLeft > 1;
    const toolsScrollRight = elem.scrollLeft + elem.clientWidth < elem.scrollWidth - 1;
    if (
      toolsScrollLeft !== this.state.toolsScrollLeft ||
      toolsScrollRight !== this.state.toolsScrollRight
    ) {
      this.setState({ toolsScrollLeft, toolsScrollRight });
    }
  };

  // The sideways attachment strip scrolls in a fixed width, and a chip can end
  // exactly at its edge, so a "+N" after it counts the chips not fully in view
  // rather than let them look lost after a rotation. Once anything is cut off
  // it stays until everything fits, so the strip doesn't jump while scrolling.
  setStripScroller = (elem) => {
    if (this.stripScroller === elem) return;
    this.stripResizeObserver?.disconnect();
    this.stripResizeObserver = null;
    this.stripScroller = elem;
    if (!elem && this.state.stripHidden) this.setState({ stripHidden: 0 });
    if (elem && typeof ResizeObserver !== 'undefined') {
      this.stripResizeObserver = new ResizeObserver(this.updateStripOverflow);
      this.stripResizeObserver.observe(elem);
      // The chips' row: it changes size when a chip comes or goes.
      if (elem.firstElementChild) this.stripResizeObserver.observe(elem.firstElementChild);
    }
  };

  getStripChips = () => {
    const elem = this.stripScroller;
    if (!elem) return { box: null, chips: [] };
    const box = elem.getBoundingClientRect();
    const chips = [...(elem.firstElementChild?.children || [])].map((chip) => {
      const rect = chip.getBoundingClientRect();
      return { rect, cut: rect.left < box.left - 1 || rect.right > box.right + 1 };
    });
    return { box, chips };
  };

  updateStripOverflow = () => {
    const stripHidden = this.getStripChips().chips.filter((chip) => chip.cut).length;
    if (stripHidden !== this.state.stripHidden) this.setState({ stripHidden });
  };

  // Brings the next chip cut off on the right fully into view, or goes back to
  // the start once the end is reached.
  scrollStrip = () => {
    const elem = this.stripScroller;
    const { box, chips } = this.getStripChips();
    if (!elem || !box) return;
    const next = chips.find(({ rect }) => rect.right > box.right + 1);
    // The row's own padding, so the chip's X keeps its hit area in view.
    if (next) elem.scrollBy({ left: next.rect.right + 6 - box.right, behavior: 'smooth' });
    else elem.scrollTo({ left: 0, behavior: 'smooth' });
  };

  renderStripCount = () => {
    const count = this.state.stripHidden;
    if (!count) return null;
    const base =
      'relative flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-muted px-2 text-xs font-medium tabular-nums text-muted-foreground active:bg-accent';
    // Pointer and touch only: VoiceOver and Tab reach every chip, which
    // scrolls it into view.
    return (
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={this.scrollStrip}
        className={cn(base, touchHitArea(base, { coarse: true }), 'ml-0.5 mr-2')}
      >
        +{count}
      </button>
    );
  };

  scrollTools = (direction) => {
    const elem = this.toolsScroller;
    if (!elem) return;
    elem.scrollBy({ left: direction * Math.max(elem.clientWidth * 0.8, 80), behavior: 'smooth' });
  };

  renderToolsScrollButton = (direction) => {
    const visible = direction > 0 ? this.state.toolsScrollRight : this.state.toolsScrollLeft;
    if (!visible) return null;
    const Icon = direction > 0 ? ChevronRight : ChevronLeft;
    // Pointer and touch only: Tab already walks every tool and scrolls it into view.
    return (
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={() => this.scrollTools(direction)}
        className={cn(
          'absolute inset-y-0 hidden w-9 items-center text-muted-foreground hover:text-foreground max-xl:flex',
          direction > 0
            ? 'right-0 justify-end bg-gradient-to-l from-background from-40% to-transparent'
            : 'left-0 justify-start bg-gradient-to-r from-background from-40% to-transparent',
        )}
      >
        <Icon className="size-4" />
      </button>
    );
  };

  getCurrentAiDraft = () => {
    const convId = this.props.activeConversation?._id;
    return convId ? this.props.aiDraftByConversation?.[convId] : null;
  };

  getAiDraftId = (draft) => aiDraftId(draft);

  // The unsent composer text and the AI draft's Use marker live in localStorage,
  // which can be blocked.
  composerStorage = () => {
    try {
      return window.localStorage;
    } catch {
      return null;
    }
  };

  // Use on the draft card: the draft goes into the reply composer for a final
  // edit, with a marker saying Use put it there. The card has already asked
  // before replacing anything typed.
  applyAiDraft = (draft = this.getCurrentAiDraft()) => {
    const convId = this.props.activeConversation?._id;
    if (!convId) return;
    const used = putDraftInComposer(this.composerStorage(), convId, draft);
    if (!used) return;

    this.setState({ composerMode: 'reply', ...used }, () => {
      const el = document.getElementById('chatBoxTextBox');
      if (el) el.focus();
    });
  };

  // reason: the code picked on the card's dismiss menu.
  dismissAiDraft = async (reason) => {
    const draft = this.getCurrentAiDraft();
    const convId = this.props.activeConversation?._id;
    // A note-only draft (the AI couldn't answer) is dismissed the same way.
    if (!hasAiDraftContent(draft) || !convId) return;

    const { dismissed, clearComposer } = await dismissDraftFromCard({
      post: (body) =>
        axios.post(`${this.webchatApiUrl}/api/webchat/ai-draft/dismiss`, body, {
          withCredentials: true,
        }),
      storage: this.composerStorage(),
      conversationId: convId,
      businessId: this.props.activeConversation._business,
      draft,
      reason,
      composerText: this.state.message,
    });
    // The route answers { error: true } when it could not record it: keep the card.
    if (!dismissed) {
      toast.error('Failed to dismiss AI draft');
      return;
    }

    this.props.clearAiDraft({ _customerConversation: convId });
    if (clearComposer) {
      this.setState({ message: '', activeAiDraftId: null });
    } else {
      this.setState({ activeAiDraftId: null });
    }
  };

  _pasteHandler = (e) => {
    if (e.clipboardData?.items.length) {
      const text = e.clipboardData.getData('text');

      if (this.state.message) {
        this.setState({ message: e.target.value });
      }

      this._transformImages(e.clipboardData, text);
    }
  };

  _isValidFormat = (fileType) => acceptedFileTypes.includes(fileType);

  _transformImages = (data) => {
    // NOTE: This needs to be a for loop, it's a list like object
    for (let i = 0; i < data.items.length; i += 1) {
      if (this._isValidFormat(data.items[i].type)) {
        const blob = data.items[i].getAsFile();

        const { URL } = window;

        const file = new File([blob], 'pasted-image.png');

        if (blob) {
          // We shouldn't fire the callback if we can't create `new Blob()`
          const src = URL.createObjectURL(blob);

          this._setImagesFromPaste(src, file);
        }
      }
    }
  };

  // Adds dropped or picked files to the ones already attached. Attachments are
  // matched by path elsewhere, so a file that's already attached is skipped.
  addAttachments = async (files) => {
    if (this.state.composerMode === 'note') return this.addNoteAttachments(files);

    const current = this.state.attachments;
    const newFiles = files.filter((file) => !current.some((a) => a.path && a.path === file.path));

    if (current.length + newFiles.length > MAX_ATTACHMENTS) {
      return this.setState({
        errorMessage: TOO_MANY_ATTACHMENTS_MESSAGE,
        errorTitle: 'Cannot attach file',
        openErrorDialog: true,
      });
    }

    const added = newFiles.map((file) =>
      Object.assign(file, { preview: URL.createObjectURL(file), loading: true }),
    );
    this.setState({ attachments: [...current, ...added] });

    for await (const file of added) {
      await this.uploadAttachment(file);
    }
  };

  _setImagesFromPaste = (_url, file) => {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      return this.setState({
        errorMessage: 'That image is too large to send. Please use an image smaller than 20mb.',
        errorTitle: 'Image too large',
        openErrorDialog: true,
      });
    }

    this.setState({
      attachments: [
        Object.assign(file, {
          preview: URL.createObjectURL(file),
          loading: true,
        }),
      ],
    });

    this.uploadAttachment(file);
  };

  // Phones, including one held sideways: Return adds a new line and the send
  // button sends, as in Messages. Cmd/Ctrl+Return on a hardware keyboard still sends.
  usesReturnForNewLine = () =>
    Boolean(this.props.isMobile) ||
    (isTouchViewport() && Math.min(window.screen.width, window.screen.height) < 500);

  _handleKeyDown = (e) => {
    // for other devs who might not know keyCodes
    const ENTER_KEY = 13;

    if (e.keyCode === ENTER_KEY && !e.metaKey && !e.ctrlKey && this.usesReturnForNewLine()) {
      return;
    }

    if (e.keyCode === ENTER_KEY && !this.state.loading) {
      const activeElement = document.activeElement;
      const chatBoxTextarea = document.getElementById('chatBoxTextBox');

      // FIRST: Check if ChatBox textarea is NOT the active element
      if (activeElement !== chatBoxTextarea) {
        // ChatBox is not focused, don't handle Enter at all
        return;
      }

      // SECOND: Ignore Enter events that happen immediately after focus (likely from customer selection)
      if (this.lastFocusTime && Date.now() - this.lastFocusTime < 150) {
        // Too soon after focus, this is likely a stale event from customer selection
        return;
      }

      // THIRD: Even if ChatBox is focused, check if there's a react-select menu open
      // This handles the case where ChatBox might still have focus but user is hovering over search results
      const selectMenu = document.querySelector('[class*="react-select__menu"]');
      if (selectMenu) {
        const hasOptions = selectMenu.querySelector('[class*="react-select__option"]');
        if (hasOptions) {
          // Menu is open with selectable options, don't send message
          return;
        }
      }

      // if user is holding shift/alt, insert a newline at the caret position
      if (e.shiftKey || e.altKey) {
        e.preventDefault();
        const el = this.chatboxElement;
        if (!el || el.readOnly) {
          return;
        }
        const { selectionStart, selectionEnd } = el;
        const current = this.state.message || '';
        const before = current.slice(0, selectionStart);
        const after = current.slice(selectionEnd);
        const newMessage = `${before}\n${after}`;
        return this.setState({ message: newMessage }, () => {
          const caret = (selectionStart || 0) + 1;
          if (this.chatboxElement) {
            this.chatboxElement.focus();
            this.chatboxElement.setSelectionRange(caret, caret);
            this.autoResizeTextarea(this.chatboxElement);
          }
          if (this.props.activeConversation?._id) {
            try {
              localStorage.setItem(`message-${this.props.activeConversation._id}`, newMessage);
            } catch {
              /* no-op */
            }
          }
        });
      }

      // plain Enter sends the message (or saves the note in Note mode)
      e.preventDefault();
      return this.state.composerMode === 'note' ? this.sendNoteMessage() : this.sendTextMessage();
    }
  };

  setComposerMode = (mode) => {
    if (mode === this.state.composerMode) {
      return;
    }
    this.setState({ composerMode: mode });
    const el = document.getElementById('chatBoxTextBox');
    if (el) {
      el.focus();
    }
  };

  noteDraftKey = () => `inline-note:${this.props.auth?.user?._id}:${this.props.activeConversation?._id}`;
  saveNoteDraft = (draft) => {
    try {
      sessionStorage.setItem(
        this.noteDraftKey(),
        JSON.stringify({ ...draft, attachments: storableNoteAttachments(draft.attachments) }),
      );
    } catch { /* keep in memory */ }
  };
  restoreNoteDraft = () => {
    let draft = { text: '', mentions: [], requestId: crypto.randomUUID() };
    try { const saved = sessionStorage.getItem(this.noteDraftKey()); if (saved) draft = JSON.parse(saved); } catch { /* optional storage */ }
    this.setState({ noteDraft: draft });
    // Photos kept from before a reload have no in-memory preview; show the stored one.
    for (const attachment of draft.attachments || []) {
      if (!isImageAttachment(attachment) || attachment.preview) continue;
      getNoteAttachmentUrl(attachment.key, { name: attachment.name })
        .then((preview) => this.patchNoteAttachment(attachment.path, { preview }, { sameRequest: true }))
        .catch(() => {});
    }
  };
  changeNoteDraft = (text, mentions) => {
    this.setState((prev) => {
      const draft = { ...prev.noteDraft, text, mentions, requestId: crypto.randomUUID() };
      this.saveNoteDraft(draft);
      return { noteDraft: draft };
    });
  };

  // A note's photos and files live on the note draft, apart from the reply's
  // attachments, so a private note file can never be sent to the customer.
  getNoteAttachments = () => this.state.noteDraft?.attachments || [];

  // Any change but upload progress makes it a new save request.
  setNoteAttachments = (update, { sameRequest = false } = {}) => {
    this.setState((prev) => {
      const current = prev.noteDraft || { text: '', mentions: [], requestId: crypto.randomUUID() };
      const draft = {
        ...current,
        attachments: update(current.attachments || []),
        requestId: sameRequest ? current.requestId : crypto.randomUUID(),
      };
      this.saveNoteDraft(draft);
      return { noteDraft: draft };
    });
  };

  patchNoteAttachment = (path, patch, options) =>
    this.setNoteAttachments(
      (attachments) => attachments.map((a) => (a.path === path ? { ...a, ...patch } : a)),
      options,
    );

  removeNoteAttachment = (attachment) =>
    this.setNoteAttachments((attachments) => attachments.filter((a) => a.path !== attachment.path));

  addNoteAttachments = async (files) => {
    const { activeConversation } = this.props;
    const _customer = activeConversation?._customer?._id;
    const _business = activeConversation?._business;

    if (!activeConversation?._id || !_customer) {
      return this.setState({
        errorMessage: 'Notes can only be added inside an existing customer conversation.',
        errorTitle: 'Cannot attach file',
        openErrorDialog: true,
      });
    }

    if (this.getNoteAttachments().length + files.length > MAX_ATTACHMENTS) {
      return this.setState({
        errorMessage: TOO_MANY_ATTACHMENTS_MESSAGE,
        errorTitle: 'Cannot attach file',
        openErrorDialog: true,
      });
    }

    if (files.some((file) => file.size > MAX_ATTACHMENT_BYTES)) {
      return this.setState({
        errorMessage: 'Attachments must be smaller than 20mb.',
        errorTitle: 'Cannot attach file',
        openErrorDialog: true,
      });
    }

    const draftKey = this.noteDraftKey();
    // Uploads that finish after a switch to another conversation are dropped.
    const stillHere = () => this.noteDraftKey() === draftKey;
    const added = files.map((file) => ({
      file,
      attachment: {
        path: crypto.randomUUID(),
        name: file.name || 'Attachment',
        type: file.type,
        size: file.size,
        preview: URL.createObjectURL(file),
        loading: true,
      },
    }));
    this.setNoteAttachments((attachments) => [...attachments, ...added.map((a) => a.attachment)]);

    await Promise.all(
      added.map(async ({ file, attachment }) => {
        try {
          const uploaded = await uploadNoteAttachment(file, {
            _business,
            _customer,
            onProgress: (uploadProgress) => {
              if (stillHere()) this.patchNoteAttachment(attachment.path, { uploadProgress }, { sameRequest: true });
            },
          });
          if (stillHere()) {
            this.patchNoteAttachment(attachment.path, { ...uploaded, loading: false, uploadProgress: undefined });
          }
        } catch (error) {
          if (stillHere()) this.removeNoteAttachment(attachment);
          toast.error(
            error?.isAxiosError
              ? `${attachment.name} could not be uploaded. Please try again.`
              : error?.message || `${attachment.name} could not be uploaded. Please try again.`,
          );
        }
      }),
    );
  };

  // Pasting a screenshot or file into a note attaches it, as in Reply.
  handleNotePaste = (event) => {
    const files = Array.from(event.clipboardData?.files || []).filter((file) =>
      Object.hasOwn(attachmentAcceptConfig, file.type),
    );
    if (!files.length) return;
    event.preventDefault();
    event.stopPropagation();
    this.addNoteAttachments(files);
  };

  canSaveNote = () => {
    const text = this.state.noteDraft?.text || '';
    const attachments = this.getNoteAttachments();
    return (
      (Boolean(text.trim()) || attachments.length > 0) &&
      text.length <= 10000 &&
      !attachments.some((attachment) => attachment.loading) &&
      !this.state.savingNote
    );
  };

  // The composer's attachments: the note's own files in Note mode.
  getComposerAttachments = () =>
    this.state.composerMode === 'note' ? this.getNoteAttachments() : this.state.attachments;

  removeComposerAttachment = (attachment) =>
    this.state.composerMode === 'note'
      ? this.removeNoteAttachment(attachment)
      : this.removeAttachment(attachment);

  // Save an internal team note. Notes never reach the customer; they only
  // require an existing conversation and reuse the optimistic-bubble flow.
  sendNoteMessage = async () => {
    const { activeConversation } = this.props;
    const draft = this.state.noteDraft || { text: '', mentions: [], requestId: crypto.randomUUID() };
    const text = draft.text || '';
    const attachments = draft.attachments || [];
    const draftKey = this.noteDraftKey();
    if (this.noteSaving) return;

    // Enter skips the disabled Add note button, so wait for uploads here too
    if (attachments.some((attachment) => attachment.loading)) {
      toast('Your files are still uploading.');
      return;
    }

    this.noteSaving = true;

    if (!text.trim() && !attachments.length) {
      const el = document.getElementById('chatBoxTextBox');
      if (el) el.focus();
      this.noteSaving = false;
      return this.setState({
        loading: false,
        errorMessage: 'Please enter a note.',
        errorTitle: 'Empty note',
        openErrorDialog: true,
      });
    }

    if (!activeConversation?._id || !activeConversation?._customer?._id) {
      this.noteSaving = false;
      return this.setState({
        loading: false,
        errorMessage: 'Notes can only be added inside an existing customer conversation.',
        errorTitle: 'Cannot add note',
        openErrorDialog: true,
      });
    }

    this.setState({ savingNote: true });
    try {
    this.sendTypingIndicator(false);

    const snapshotMessage = text;
    const clientId = draft.requestId;

    this.props.addOptimisticMessage({
      _conversation: activeConversation._id,
      message: {
        _id: clientId,
        clientId,
        status: 'sending',
        sender: 'note',
        text: snapshotMessage,
        mentions: draft.mentions,
        // Keeps the local previews, so photos show while the note saves
        attachments,
        createdAt: new Date().toISOString(),
        _user: this.props.auth?.user?._id,
        _customer: activeConversation._customer?._id,
        _business: activeConversation._business,
      },
    });

    const noteResponse = await this.props.sendNote({
      text: snapshotMessage,
        mentions: draft.mentions,
      attachments: attachments.map(({ key, name, type, size }) => ({ key, name, type, size })),
      clientId,
      requestId: draft.requestId,
      // Capture the conversation context up-front so the persisted note targets
      // the same conversation as the optimistic bubble, even if the user
      // switches conversations during the network round-trip.
      _conversation: activeConversation._id,
      _customer: activeConversation._customer._id,
      _business: activeConversation._business,
    });

    if (noteResponse?.error) {
      toast.error(noteResponse.message || 'Note not saved — click the bubble to retry');
    }

    this.noteSaving = false;
    if (!noteResponse?.error) {
      try { sessionStorage.removeItem(draftKey); } catch { /* optional storage */ }
      if (this.props.activeConversation?._id === activeConversation._id && this.state.noteDraft?.requestId === draft.requestId) this.setState({ noteDraft: { text: '', mentions: [], requestId: crypto.randomUUID() } });
    }
    return noteResponse;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Note not saved. Your draft is safe.');
      return { error: true };
    } finally { this.noteSaving = false; this.setState({ savingNote: false }); }
  };

  handleTextFieldFocused = () => {
    // Track when the field was focused to prevent processing stale events
    this.lastFocusTime = Date.now();
    document.addEventListener('keydown', this._handleKeyDown);
  };

  handleTextFieldBlurred = () => {
    document.removeEventListener('keydown', this._handleKeyDown);
  };

  lastTextChangeTime = new Date();

  diffSeconds = (dt2 = new Date(), dt1 = new Date()) => {
    return (dt2.getTime() - dt1.getTime()) / 1000;
  };

  sendTypingIndicator = (isTyping) => {
    if (isSignupSendingBlocked(this.props.auth)) return;
    const _customerConversation = this.props.activeConversation?._id;

    if (!_customerConversation) {
      return;
    }

    if (!isTyping) {
      this.lastTextChangeTime = new Date(0);
    }

    try {
      axios
        .post(`${this.webchatApiUrl}/api/webchat/typing`, {
          isTyping,
          whoIsTyping: this.props.auth.user.name,
          _customerConversation,
          at: Date.now(),
        })
        .catch((error) => {
          console.error('Error sending typing indicator:', error);
        });
    } catch (error) {
      console.error('Error sending typing indicator:', error);
    }
  };

  onTextChange = (e) => {
    const len = e.target.value.length;

    if (len > 1600) {
      return;
    }

    // save message to local storage, with a key of the conversation id
    // this will allow us to save the message if the user navigates away from the page
    // and comes back to the same conversation
    localStorage.setItem(`message-${this.props.activeConversation._id}`, e.target.value);
    if (!e.target.value) forgetUsedDraft(this.composerStorage(), this.props.activeConversation._id);

    this.setState({
      message: e.target.value,
      // Clearing the composer puts a used draft back on its card.
      ...(e.target.value ? {} : { activeAiDraftId: null }),
    });

    // we can only have typing indicators on known conversations
    if (this.props.activeConversation._id) {
      if (!len) {
        this.sendTypingIndicator(false);
        return;
      }

      if (
        this.diffSeconds(new Date(), this.lastTextChangeTime) === 0 ||
        this.diffSeconds(new Date(), this.lastTextChangeTime) > TYPING_HEARTBEAT_SECONDS
      ) {
        this.sendTypingIndicator(true);

        this.lastTextChangeTime = new Date();
        return;
      }
    }
  };

  onAmountChange = (e) => {
    // stripe allows a maximum of eight digits
    // if they are not using a decimal only use 6 digits (2 for decimal)
    if (!e.target.value.includes('.')) {
      if (e.target.value.length > 6 || Number.isNaN(e.target.value)) {
        return;
      }
    }

    // if they are using a decimal make sure it is in correct spot
    if (e.target.value.includes('.')) {
      if (e.target.value.length > 8) {
        return;
      }
      const regex = /^\d+(?:\.\d{0,2})$/;
      if (!regex.test(e.target.value)) {
        return;
      }
    }
    this.setState({
      amount: e.target.value,
    });
  };

  onDescriptionChange = (e) => {
    this.setState({
      description: e.target.value,
    });
  };

  onReferenceChange = (e) => {
    if (e.target.value.length > 20) {
      return;
    }
    this.setState({
      reference: e.target.value,
    });
  };

  updateMessageWithAI = (updatedMessage) => {
    this.setState({ message: updatedMessage });
  };

  handleAITypingStateChange = ({ isTyping, typingText }) => {
    this.setState({
      aiIsTyping: isTyping,
      aiTypingText: typingText || '',
    });
  };

  formatMessage = async () => {
    const { message, amount, reference, showPaymentRequestCompleted } = this.state;

    // if there is an amount and a reference number, and payment request completed is true ,
    // we have correct information, so create a invoice
    if (amount && reference && showPaymentRequestCompleted) {
      const invoiceMessage = message.replace(DESCRIPTION_TAG, this.state.description);

      const invoiceUrl = await this.createInvoice();

      if (!invoiceUrl) {
        return {
          error: 'There was an error sending your invoice. Please try again.',
        };
      }

      return { message: `${invoiceMessage} ${invoiceUrl}` };
    }

    return { message };
  };

  blockPendingSignupSend = () => {
    if (!isSignupSendingBlocked(this.props.auth)) return false;
    this.setState({ loading: false, errorTitle: 'Texting setup is not finished', errorMessage: this.props.auth?.selfServeSignup?.environment === 'staging' ? 'This is a staging test account. You can prepare a message, but customer messages are not sent.' : 'You can prepare your message here. Sending unlocks after your number is approved and ready and your first payment succeeds.', openErrorDialog: true });
    return true;
  };

  sendMessage = async (messageData) => {
    if (this.blockPendingSignupSend()) return;

    const { name, phoneEmail, activeConversation, _location } = this.props;

    const { language, message, showPaymentRequestCompleted, shouldCloseConversation, attachments } =
      this.state;

    const { tag, sendInChannel } = messageData || {};

    // Synchronous validations — keep the modal ErrorDialog here; these are
    // pre-send "you haven't filled this in yet" errors, not send failures.
    if (!message && !attachments.length && !showPaymentRequestCompleted) {
      const el = document.getElementById('chatBoxTextBox');
      if (el) el.focus();
      return this.setState({
        loading: false,
        errorMessage: 'Please select a template or enter a message.',
        errorTitle: 'Include a message!',
        openErrorDialog: true,
      });
    }

    if (showPaymentRequestCompleted && isEmpty(activeConversation) && (!phoneEmail || !name)) {
      return this.setState({
        loading: false,
        errorMessage: 'Please choose or create a customer to send an invoice.',
        errorTitle: 'Include a customer!',
        openErrorDialog: true,
      });
    }

    if (!activeConversation._id && (!phoneEmail || !name)) {
      return this.setState({
        loading: false,
        errorMessage: 'Be sure to enter your customer’s name and phone or email address.',
        errorTitle: 'Enter contact information',
        openErrorDialog: true,
      });
    }

    if (
      !activeConversation._id &&
      !phoneEmail.includes('@') &&
      !phoneEmail.includes('.') &&
      phoneEmail.length < 12
    ) {
      return this.setState({
        loading: false,
        errorMessage: 'Be sure to enter a proper phone number or email address.',
        errorTitle: 'Check contact information',
        openErrorDialog: true,
      });
    }

    // Payment request: block switching threads until this send finishes.
    // Also, payments go through createInvoice before sending, so we can't
    // eagerly clear the composer — leave the old pessimistic flow for them.
    const isPaymentSend = showPaymentRequestCompleted && this.state.amount && this.state.reference;

    if (isPaymentSend) {
      this.props.setPaymentSendInProgress?.(true);
      this.props.setLoadingNewMessage?.(true);
    }

    // Whether this send should use the optimistic-bubble flow:
    // - existing conversation (new-customer goes through a different API)
    // - not a payment request (creates invoice first)
    // - not scheduled (legitimately hasn't been sent)
    const useOptimistic =
      Boolean(activeConversation._id) && !isPaymentSend && !this.state.scheduledSendDate;

    this.sendTypingIndicator(false);

    // Snapshot what we're sending so we can eagerly clear composer state.
    const snapshotMessage = message;
    const snapshotAttachments = attachments;
    const snapshotLanguage = language;
    const snapshotShouldClose = shouldCloseConversation;
    const snapshotScheduledSendDate = this.state.scheduledSendDate;
    const snapshotAiDraftId = this.state.activeAiDraftId;
    // Sending a reply settles the conversation's active draft on the server
    // (used or not, note-only included), so the card goes whatever was typed.
    const snapshotHasAiDraft = hasAiDraftContent(this.getCurrentAiDraft());
    const clientId = useOptimistic
      ? `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      : undefined;

    if (useOptimistic) {
      // Clear composer immediately — no spinner, no latency.
      this.setState({
        message: '',
        attachments: [],
        shouldCloseConversation: false,
        scheduledSendDate: null,
        activeAiDraftId: null,
      });
      try {
        localStorage.removeItem(`message-${activeConversation._id}`);
      } catch {
        /* no-op */
      }
      forgetUsedDraft(this.composerStorage(), activeConversation._id);

      // Dispatch the optimistic bubble.
      const attachmentUrls = snapshotAttachments.filter((a) => a?.mediaUrl).map((a) => a.mediaUrl);
      const primaryAttachment = attachmentUrls[0];
      const sendsAsMedia = Boolean(primaryAttachment) && SENT_AS_MEDIA_RE.test(primaryAttachment);
      this.props.addOptimisticMessage({
        _conversation: activeConversation._id,
        message: {
          _id: clientId,
          clientId,
          status: 'sending',
          sender: 'business',
          text:
            primaryAttachment && !sendsAsMedia
              ? `${snapshotMessage} ${primaryAttachment}`.trim()
              : snapshotMessage,
          composerText: snapshotMessage,
          media: sendsAsMedia ? primaryAttachment : undefined,
          attachmentUrls,
          createdAt: new Date().toISOString(),
          _user: this.props.auth?.user?._id,
          _customer: activeConversation._customer?._id,
          _business: activeConversation._business,
        },
      });
    } else {
      this.setState({ loading: true });
    }

    try {
      const { error, message: formattedMessage } = await this.formatMessage();

      if (error) {
        const el = document.getElementById('chatBoxTextBox');
        if (el) el.focus();
        // Payment/template error — keep modal since user may need to read it.
        return this.setState({
          loading: false,
          errorMessage: error,
          errorTitle: 'Error',
          openErrorDialog: true,
        });
      }

      const attachmentUrls = snapshotAttachments.map((a) => a.mediaUrl);

      // send message to selected customer
      if (activeConversation._id) {
        const messageResponse = await this.props.sendMessage({
          type: 'message',
          text: formattedMessage,
          language: snapshotLanguage,
          attachmentUrls,
          tag,
          shouldCloseConversation: snapshotShouldClose,
          scheduledSendDate: snapshotScheduledSendDate
            ? moment(snapshotScheduledSendDate)
            : snapshotScheduledSendDate,
          sendInChannel,
          clientId,
          aiDraftId: snapshotAiDraftId,
        });

        if (messageResponse.error && messageResponse.errorTag === 'FACEBOOK_HUMAN_AGENT_EXPIRED') {
          return this.setState({
            loading: false,
            openFacebookMessageTagDialog: false,
            facebookHumanAgentRetryDraft: null,
            errorTitle: 'Cannot send message',
            errorMessage:
              messageResponse.message ||
              'Facebook could not send this message with the Human Agent tag. This tag is only allowed within 7 days of the customer’s last message.',
            openErrorDialog: true,
          });
        }

        if (messageResponse.error && messageResponse.errorTag === 'FACEBOOK_MESSAGE_TAG_INVALID') {
          return this.setState({
            loading: false,
            openFacebookMessageTagDialog: false,
            facebookHumanAgentRetryDraft: null,
            errorTitle: 'Cannot send message',
            errorMessage:
              messageResponse.message ||
              'Facebook rejected the selected message tag. Please choose a different option or ask the customer to message again to reopen the standard window.',
            openErrorDialog: true,
          });
        }

        if (messageResponse.error && messageResponse.errorTag === 'FACEBOOK_WINDOW_24_HOURS') {
          // Show the Facebook-specific confirmation dialog. We keep the
          // failed optimistic bubble in-thread so the user has a visual of
          // what they attempted to send, then we retry with HUMAN_AGENT.
          return this.setState({
            loading: false,
            openFacebookMessageTagDialog: true,
            facebookHumanAgentRetryDraft: {
              text: formattedMessage,
              language: snapshotLanguage,
              attachmentUrls,
              shouldCloseConversation: snapshotShouldClose,
              scheduledSendDate: snapshotScheduledSendDate
                ? moment(snapshotScheduledSendDate)
                : snapshotScheduledSendDate,
              sendInChannel,
              clientId,
            },
          });
        }

        if (messageResponse.error) {
          // Non-optimistic send (payments) — keep modal so the user sees it.
          // Optimistic send — surface via toast; bubble is already marked failed.
          if (useOptimistic) {
            toast.error(messageResponse.message || 'Message not sent — click the bubble to retry');
          } else {
            this.setState({
              errorMessage: messageResponse.message || 'There was an error sending your message.',
              errorTitle: 'Error',
              openErrorDialog: true,
            });
          }
          return this.setState({ loading: false });
        }

        if (!useOptimistic) {
          this.setState({
            loading: false,
            message: '',
            attachments: [],
            shouldCloseConversation: false,
            scheduledSendDate: null,
            activeAiDraftId: null,
          });
          localStorage.removeItem(`message-${activeConversation._id}`);
          forgetUsedDraft(this.composerStorage(), activeConversation._id);
        } else {
          this.setState({ loading: false, activeAiDraftId: null });
        }
        if (snapshotAiDraftId || snapshotHasAiDraft) {
          this.props.clearAiDraft({
            _customerConversation: activeConversation._id,
          });
        }

        if (snapshotScheduledSendDate) {
          this.props.refreshScheduledMessages?.();
        }

        return this.props.resetNameAndPhoneEmail();
      }

      // New-customer path — still pessimistic for now.
      const response = await this.props.sendMessageToNewCustomer({
        type: 'message',
        text: formattedMessage,
        language: snapshotLanguage,
        name,
        phone: phoneEmail,
        _location,
        attachmentUrls,
        shouldCloseConversation: snapshotShouldClose,
        scheduledSendDate: snapshotScheduledSendDate,
      });

      if (response.error) {
        return this.setState({ loading: false });
      }

      this.setState({
        loading: false,
        message: '',
        attachments: [],
        shouldCloseConversation: false,
        scheduledSendDate: null,
      });
      try {
        localStorage.removeItem(`message-${activeConversation._id}`);
      } catch {
        /* no-op */
      }

      return this.props.resetNameAndPhoneEmail();
    } finally {
      if (isPaymentSend) {
        this.props.setPaymentSendInProgress?.(false);
        this.props.setLoadingNewMessage?.(false);
      }
    }
  };

  sendTextMessage = ({
    sendInChannel = undefined, // by default, ignore this parameter, but it can be set to 'phone' or 'email'
  } = {}) => {
    // they have entered information for the payment request but have not actually added it
    if (
      this.state.amount &&
      this.state.reference &&
      this.state.message.length &&
      !this.state.showPaymentRequestCompleted
    ) {
      return this.setState({
        loading: false,
        errorMessage: 'Please ADD or close your payment request.',
        errorTitle: 'Error!',
        openErrorDialog: true,
      });
    }

    if (
      !(this.state.message || this.state.message.length) &&
      !(this.state.attachments || this.state.attachments.length) &&
      !this.state.showPaymentRequestCompleted
    ) {
      return this.setState({
        loading: false,
        errorMessage: 'Please enter a message or include an attachment.',
        errorTitle: 'Missing message!',
        openErrorDialog: true,
      });
    }

    this.setState({ templatesExpanded: false });

    return this.sendMessage({ sendInChannel });
  };

  scheduleMessage = () => {
    return this.sendTextMessage();
  };

  toggleEmojiPicker = () => {
    this.setState({
      showEmojiPicker: !this.state.showEmojiPicker,
      templatesExpanded: false,
      paymentsExpanded: false,
    });
  };

  insertVideoChatText = () => {
    this.setState({
      message: 'Please join my video chat by clicking the following link: ((video_chat))',
    });
  };

  toggleDatePicker = () => {
    const currentDate = moment().add(2, 'hour').startOf('hour').format('YYYY-MM-DDTHH:mm');
    this.setState({
      scheduledSendDate: currentDate,
    });
  };

  handleDateChange = (event) => {
    const date = moment(event.target.value).format('YYYY-MM-DDTHH:mm');
    this.setState({ scheduledSendDate: date });
  };

  // Cancel, Escape or a drag down forgets the time, so the next send goes now.
  cancelSchedule = () => {
    this.setState({ scheduleOpen: null, scheduledSendDate: null });
  };

  // Closes the step itself: the dialog's own close would run cancelSchedule.
  confirmSchedule = (event) => {
    event?.preventDefault?.();
    this.setState({ scheduleOpen: null });
    this.scheduleMessage();
  };

  handleScheduleOpenChange = (from) => (open) => {
    if (open) this.setState({ scheduleOpen: from });
    else this.cancelSchedule();
  };

  renderScheduleTimeFields = (id) => (
    <div className="space-y-3">
      <QuickScheduleOptions
        selectedDate={this.state.scheduledSendDate}
        onSelect={(date) =>
          this.setState({
            scheduledSendDate: moment(date).format('YYYY-MM-DDTHH:mm'),
          })
        }
      />
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground">Or choose a date and time</p>
        <Input
          ref={this.datePickerRef}
          type="datetime-local"
          id={id}
          min={moment().format('YYYY-MM-DDTHH:mm')}
          onChange={this.handleDateChange}
          value={this.state.scheduledSendDate}
          className="max-md:h-11 max-md:text-base"
        />
      </div>
    </div>
  );

  renderSchedulePreview = () => {
    const { scheduledSendDate } = this.state;

    const isFutureDate = scheduledSendDate && new Date(scheduledSendDate) >= new Date();

    if (!isFutureDate) {
      return <p className="text-sm text-muted-foreground">Choose a future date and time.</p>;
    }

    const friendlyDate = moment(scheduledSendDate).calendar(null, {
      sameDay: '[today at] LT',
      nextDay: '[tomorrow at] LT',
      nextWeek: 'dddd [at] LT',
      sameElse: 'll [at] LT',
    });

    return <p className="text-sm text-muted-foreground">Will send {friendlyDate}.</p>;
  };

  scheduledIconDisabled = () => {
    if (this.isGroupContext()) {
      return true;
    }

    const { auth, activeConversation } = this.props;

    const { isEnterpriseEnabled } = auth.user;

    if (isEnterpriseEnabled) {
      const { businessGroup } = this.props;

      if (activeConversation?._business) {
        const currentBusiness = businessGroup.filter(
          (business) => business._id === activeConversation._business,
        )[0];

        if (currentBusiness.products.includes('message-scheduler')) {
          return false;
        }
      }

      return true;
    }

    const activeConversationIsFacebookOrInstagram =
      activeConversation?._customer &&
      (activeConversation._customer.facebookUserId || activeConversation._customer.instagramUserId);
    if (!auth?.products?.includes('message-scheduler') || activeConversationIsFacebookOrInstagram) {
      return true;
    }

    return false;
  };

  // if business does not have the video-chat product, disable the button
  videoChatIconDisabled = () => {
    return this.props.auth.products && !this.props.auth.products.includes('video-chat');
  };

  renderDatePicker = () => {
    return (
      <DropdownMenuItem onClick={this.toggleDatePicker} disabled={this.isGroupContext()}>
        Schedule message
      </DropdownMenuItem>
    );
  };

  addEmoji = (emoji) => {
    if (this.state.composerMode === 'note') {
      this.changeNoteDraft(`${this.state.noteDraft?.text || ''}${emoji.native}`, this.state.noteDraft?.mentions || []);
      this.setState({ showEmojiPicker: false }); return;
    }
    const { message } = this.state;
    const newMessage = `${message}${emoji.native}`;
    this.setState({ message: newMessage, showEmojiPicker: false });
    const el = document.getElementById('chatBoxTextBox');

    if (el) {
      el.focus();
    }
  };

  // Downscales large images in the browser, then uploads the file straight to
  // S3 with a presigned URL. The resulting public mediaUrl is attached to the
  // outgoing message for whichever channel delivers it (webchat widget,
  // SMS/MMS, WhatsApp).
  uploadAttachment = async (attachment) => {
    try {
      this.setState({ loading: true });

      // keep `attachment` itself for the state matching below — only the
      // uploaded bytes change
      const uploadFile = await downscaleImage(attachment);

      const s3UploadRes = await axios.get('/api/presigned-upload-url', {
        params: {
          fileType: uploadFile.type,
          fileName: uploadFile.name,
          fileSize: uploadFile.size,
        },
      });

      if (!s3UploadRes.data || s3UploadRes.data.error) {
        this.setState({
          loading: false,
          errorMessage:
            'There was an error uploading your image. Please shrink the file and try again.',
          errorTitle: 'Error',
          openErrorDialog: true,
        });

        return null;
      }

      const { uploadUrl, mediaUrl, contentType } = s3UploadRes.data;

      // upload directly to s3; the type must match the one the URL was signed for.
      // S3 rejects the app-wide JWT header and credentials, and large files can
      // outlast the default timeout.
      await axios.put(uploadUrl, uploadFile, {
        headers: { 'Content-Type': contentType || uploadFile.type, Authorization: null },
        withCredentials: false,
        timeout: 0,
        _suppressErrorToast: true,
        onUploadProgress: (event) => {
          if (event.total) {
            this.setAttachmentProgress(attachment, Math.min(1, event.loaded / event.total));
          }
        },
      });

      this.setState({
        attachments: this.state.attachments.map((a) => {
          if (attachment.path) {
            return a.path === attachment.path
              ? Object.assign(a, {
                  preview: URL.createObjectURL(a),
                  loading: false,
                  mediaUrl,
                })
              : a;
          }

          return a.name === attachment.name
            ? Object.assign(a, {
                preview: URL.createObjectURL(a),
                loading: false,
                mediaUrl,
              })
            : a;
        }),
      });

      this.setState({
        loading: false,
      });
    } catch (err) {
      console.log('upload error', err);
      this.setState({
        loading: false,
        errorMessage:
          'There was an error sending your image. It is likely because your image file is too big. Please shrink the file and try again.',
        errorTitle: 'Error',
        openErrorDialog: true,
      });

      return null;
    }
  };

  // Create an invoice on the backend
  createInvoice = async () => {
    try {
      const { amount, reference, description, language, includeConvenienceFee } = this.state;
      const { name, phoneEmail, activeConversation, _location } = this.props;
      const _customer = activeConversation?._customer?._id;
      // in the compose flow activeConversation is empty, so fall back to the
      // selected location (same source the message send uses) to ensure
      // enterprise invoices are created under the correct business
      const _business = activeConversation?._business || _location;

      const requestBody = {
        amount,
        referenceNumber: reference,
        description,
        language,
        _customer,
        customerName: name,
        customerPhoneEmail: phoneEmail,
        _business,
        includeConvenienceFee,
      };

      const res = await axios.post('/api/payments/create-invoice', requestBody);

      const { customer, invoiceUrl } = res.data;

      // update the recent payments (for messenger) — optional in compose, where
      // these refreshers aren't wired (we graduate into the conversation on send)
      await this.props.fetchCustomerRecentInvoices?.(customer._id);
      // update the invoice table
      await this.props.fetchRecentInvoices?.();

      this.setState({
        amount: '',
        reference: '',
        description: '',
        showPaymentRequestCompleted: false,
        paymentsExpanded: false,
        includeConvenienceFee: false,
      });

      return invoiceUrl;
    } catch {
      return null;
    }
  };

  renderErrorDialog = () => (
    <ErrorDialog
      open={this.state.openErrorDialog}
      message={this.state.errorMessage}
      title={this.state.errorTitle}
      handleCloseErrorDialog={this.handleCloseErrorDialog}
    />
  );

  handleCloseFacebookMessageTagDialog = () => {
    this.setState({
      openFacebookMessageTagDialog: false,
      facebookHumanAgentRetryDraft: null,
    });
  };

  sendFacebookWithHumanAgent = async () => {
    if (this.blockPendingSignupSend()) return;
    const retryDraft = this.state.facebookHumanAgentRetryDraft;
    if (!retryDraft) return;

    this.setState({ loading: true });

    const messageResponse = await this.props.sendMessage({
      type: 'message',
      ...retryDraft,
      tag: 'HUMAN_AGENT',
    });

    // Close the dialog regardless; success will reconcile and failure will show an error.
    this.setState({
      openFacebookMessageTagDialog: false,
      facebookHumanAgentRetryDraft: null,
      loading: false,
    });

    if (messageResponse?.error && messageResponse.errorTag === 'FACEBOOK_HUMAN_AGENT_EXPIRED') {
      return this.setState({
        errorTitle: 'Cannot send message',
        errorMessage:
          messageResponse.message ||
          'Facebook could not send this message with the Human Agent tag. This tag is only allowed within 7 days of the customer’s last message.',
        openErrorDialog: true,
      });
    }

    if (messageResponse?.error && messageResponse.errorTag === 'FACEBOOK_WINDOW_24_HOURS') {
      return this.setState({
        errorTitle: 'Cannot send message',
        errorMessage:
          messageResponse.message ||
          'Facebook could not send this message. Please ask the customer to send a new message to reopen the window.',
        openErrorDialog: true,
      });
    }

    if (messageResponse?.error) {
      return this.setState({
        errorTitle: 'Error',
        errorMessage: messageResponse.message || 'There was an error sending your message.',
        openErrorDialog: true,
      });
    }
  };

  renderFacebookMessageTagDialog = () => (
    <FacebookMessageTagDialog
      open={this.state.openFacebookMessageTagDialog}
      onSendWithHumanAgent={this.sendFacebookWithHumanAgent}
      handleCloseFacebookMessageTagDialog={this.handleCloseFacebookMessageTagDialog}
    />
  );

  toggleTemplates = () => {
    this.setState({
      templatesExpanded: !this.state.templatesExpanded,
      showEmojiPicker: false,
      paymentsExpanded: false,
    });
  };

  togglePayments = () => {
    this.setState((prevState) => {
      const willOpen = !prevState.paymentsExpanded;

      return {
        paymentsExpanded: willOpen,
        showEmojiPicker: false,
        templatesExpanded: false,
        showPaymentRequestCompleted: false,
        includeConvenienceFee: willOpen
          ? !!this.props.auth?.isIncludingConvenienceFeeByDefault
          : prevState.includeConvenienceFee,
      };
    });
  };

  toggleConvenienceFee = (checked) => {
    this.setState({
      includeConvenienceFee: checked,
    });
  };

  toggleAddPaymentRequestTemplate = (checked) => {
    this.setState({
      addPaymentRequestTemplate: checked,
    });
  };

  handlePaymentAccept = async () => {
    const { amount, reference, description } = this.state;
    const { _customer } = this.props.activeConversation;

    if (_customer) {
      const { phone, facebookUserId } = _customer;

      // if they are sending a payment request to a facebook user don't let them
      if (facebookUserId && !phone) {
        return this.setState({
          loading: false,
          errorMessage: 'Sorry you can not send payment requests through Facebook.',
          errorTitle: ' Wrong customer',
          openErrorDialog: true,
        });
      }
    }

    // don't accept payment if all fields are not filled out
    if (!(amount && reference && description)) {
      return this.setState({
        loading: false,
        errorMessage: 'Please enter all payment fields.',
        errorTitle: 'Missing payment fields!',
        openErrorDialog: true,
      });
    }
    // if they some how put in not a number
    if (Number.isNaN(amount)) {
      return this.setState({
        loading: false,
        errorMessage: 'Please enter a correct number.',
        errorTitle: 'Incorrect payment fields!',
        openErrorDialog: true,
      });
    }

    if (amount < 1 || amount > 999999.99) {
      return this.setState({
        loading: false,
        errorMessage: 'Please enter a number between 1 and 999,999',
        errorTitle: 'Incorrect payment fields!',
        openErrorDialog: true,
      });
    }

    if (amount.length > 8 || Number.isNaN(amount)) {
      return this.setState({
        loading: false,
        errorMessage: 'Please enter a correct number.',
        errorTitle: 'Incorrect payment fields!',
        openErrorDialog: true,
      });
    }

    // if they are using a decimal make sure it is in correct spot
    if (amount.includes('.')) {
      const regex = /^\d+(?:\.\d{0,2})$/;
      if (!regex.test(amount)) {
        return this.setState({
          loading: false,
          errorMessage: 'Please enter a correct number.',
          errorTitle: 'Incorrect payment fields!',
          openErrorDialog: true,
        });
      }
      // this will ensure correct decimal places
      this.setState({
        amount: Number.parseFloat(this.state.amount).toFixed(2),
      });
    }

    // If the user wants to add the Payment Request template, find it and add it to the message
    if (this.state.addPaymentRequestTemplate) {
      const { templates } = this.props;
      const paymentRequestTemplate = templates.find(
        (template) => template.title === 'Payment Request',
      );

      if (paymentRequestTemplate) {
        this.setState({
          message: paymentRequestTemplate.text,
          language: paymentRequestTemplate.language || 'en',
        });
      }
    }

    this.setState({
      showPaymentRequestCompleted: true,
      paymentsExpanded: false,
      addPaymentRequestTemplate: false, // Reset the checkbox
    });
  };

  handlePaymentClose = () => {
    this.setState({
      showPaymentRequestCompleted: false,
      amount: '',
      reference: '',
      description: '',
    });
  };

  handlePaymentEdit = () => {
    this.setState({
      showPaymentRequestCompleted: false,
      paymentsExpanded: true,
    });
  };

  // The payment chip on a phone held sideways. With the keyboard up the form
  // covers the message field, so the caret moves to Amount in the same tap
  // (iOS keeps the keyboard up only then) and typing can't land in the
  // message. MobileViewportSync scrolls Amount into view in the composer.
  editPaymentFromChip = () => {
    const typing = this.isComposerField(document.activeElement);
    flushSync(this.handlePaymentEdit);
    if (typing) document.getElementById('amount')?.focus({ preventScroll: true });
  };

  // The amount the customer is asked for, with the 2.9% fee when it's included.
  getPaymentRequestTotal = () => {
    const amount = Number(this.state.amount);
    return this.state.includeConvenienceFee
      ? Math.round((amount + Math.round(amount * 0.029 * 100) / 100) * 100) / 100
      : amount;
  };

  handleCloseErrorDialog = () => {
    this.setState({
      errorMessage: '',
      errorTitle: '',
      openErrorDialog: false,
    });
  };

  formattedMessageTextForReviewInvite = (text) => {
    if (!text) {
      return '';
    }

    // if message already includes the review invitation tag, return the string
    if (text.includes(REVIEW_INVITATION_TAG)) {
      return text.trim();
    }

    // if message does not include review invitation tag, add it.
    return `${text.trim()} ${REVIEW_INVITATION_TAG}`;
  };

  // user has selected a template. place is into the message box formatted correctly.
  handleTemplateClick = async ({ template }) => {
    const { type, language } = template;

    // format the message if template is type review-invitation
    const message =
      type === 'review-invitation'
        ? this.formattedMessageTextForReviewInvite(template.text)
        : template.text;

    if (this.state.composerMode === 'note') {
      this.changeNoteDraft(message.replace(IMAGE_TAG, ''), []);
      this.setState({ searchedTemplates: null, templatesExpanded: false });
      return;
    }

    // set the language as saved in the template
    this.setState({
      searchedTemplates: null,
      message,
      language,
      templatesExpanded: false,
    });

    // format message if template includes custom image
    if (template.text.includes(IMAGE_TAG)) {
      // remove text ((image)) from the text
      this.setState({ message: message.replace(IMAGE_TAG, '') });

      // retrieve the template image from the backend
      this.getTemplateImage(template);
    }
  };

  getUserTemplates = async () => {
    this.props.getTemplates();
  };

  deleteTemplate = async ({ template }) => {
    try {
      const res = await axios.post('/api/template/delete', { template });

      this.props.getTemplates();

      if (res.data?.error) {
        if (res.data.message === 'No template found') {
          return;
        }
        this.setState({
          openErrorDialog: true,
          errorTitle: 'Error Deleting Template',
          errorMessage: res.data.message || 'There was an error deleting your template.',
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  // load the template image from the backend
  getTemplateImage = async (template) => {
    this.setState({ attachments: [{ loading: true }] });

    const res = await axios.post('/api/template-image-url', {
      _template: template._id,
    });

    const { templateImageUrl } = res.data;

    this.setState({
      attachments: [
        {
          mediaUrl: templateImageUrl,
          loading: false,
          path: 'template image.jpg',
        },
      ],
    });
  };

  setAttachmentProgress = (attachment, uploadProgress) => {
    this.setState({
      attachments: this.state.attachments.map((a) =>
        a === attachment ? Object.assign(a, { uploadProgress }) : a,
      ),
    });
  };

  removeAttachment = (attachment) => {
    const newAttachments = this.state.attachments.filter((a) => a.path !== attachment.path);

    this.setState({ attachments: newAttachments });
  };

  renderImagePreviews = () => {
    const previews = this.getComposerAttachments().map((attachment) => (
      <ImagePreview
        key={attachment.path}
        attachment={attachment}
        removeAttachment={this.removeComposerAttachment}
      />
    ));

    return previews.length ? (
      <div className={cn('flex flex-wrap gap-2 pb-2', SIDEWAYS_HIDE)}>{previews}</div>
    ) : null;
  };

  hasSidewaysAttachments = () =>
    this.getComposerAttachments().length > 0 ||
    (this.state.composerMode !== 'note' && Boolean(this.state.showPaymentRequestCompleted));

  // A phone held sideways (keyboard up or down): the attachments and the
  // payment request become small chips beside the field, so the field and
  // Send keep their place on the short screen. Like iOS, only a chip's X
  // removes it (a 44px target); tapping a photo does nothing, and the payment
  // chip opens its form. The full previews come back on taller screens.
  renderSidewaysAttachments = () => {
    if (!this.hasSidewaysAttachments()) return null;
    const attachments = this.getComposerAttachments();
    const showPayment = this.state.composerMode !== 'note' && this.state.showPaymentRequestCompleted;
    const total = formatAmount({ amount: this.getPaymentRequestTotal() * 100 });
    const removeBase =
      'relative flex w-8 shrink-0 items-center justify-center self-stretch rounded-r-lg active:bg-accent';
    const removeClass = cn(removeBase, touchHitArea(removeBase, { coarse: true }));
    // The X's hit area reaches 6px past its own box, so the amount and the photo
    // keep that much padding and nothing they show is under it.
    const editBase = 'relative flex items-center gap-1 rounded-l-lg pl-2 pr-1.5 text-xs font-medium active:bg-accent';
    const removeBadge = (
      <span
        className="flex size-[18px] items-center justify-center rounded-full bg-foreground/75 text-background"
        aria-hidden
      >
        <X className="size-3" strokeWidth={3} />
      </span>
    );

    return (
      <div
        role="group"
        aria-label="Attachments"
        // A tap in here keeps the text field focused, so the keyboard stays
        // up (the payment chip moves it to the form's Amount).
        onMouseDown={(event) => event.preventDefault()}
        className={cn(
          SIDEWAYS_SHOW,
          'max-w-52 items-center self-center kb-open:max-xl:[@media(max-height:560px)]:max-w-44 kb-open:max-xl:[@media(max-height:560px)]:self-end',
        )}
      >
        <div
          ref={this.setStripScroller}
          onScroll={this.updateStripOverflow}
          // Tab can land on a chip's X that is only partly in view (browsers
          // don't scroll then); bring the whole control in.
          onFocus={(event) => event.target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })}
          className="scrollbar-none min-w-0 overflow-x-auto"
        >
          <div className="flex w-max items-center gap-2 px-1.5">
            {showPayment && (
              <div className="relative flex h-11 shrink-0 items-stretch rounded-lg border bg-card text-card-foreground">
                <button
                  type="button"
                  aria-label={`Edit payment request, ${total}`}
                  onClick={this.editPaymentFromChip}
                  className={cn(editBase, touchHitArea(editBase, { coarse: true }))}
                >
                  <CreditCard className="size-3.5 text-muted-foreground" aria-hidden />
                  {total}
                </button>
                <button
                  type="button"
                  aria-label="Remove payment request"
                  onClick={this.handlePaymentClose}
                  className={removeClass}
                >
                  {removeBadge}
                </button>
              </div>
            )}
            {attachments.map((attachment) => {
              const name = attachment.name || attachment.path || 'attachment';
              const isImage =
                attachment.type?.startsWith('image/') ||
                checkIsImage(attachment.path) ||
                checkIsImage(attachment.name) ||
                checkIsImage(attachment.mediaUrl);

              return (
                <div
                  key={attachment.path}
                  className="relative flex h-11 shrink-0 items-stretch gap-1.5 rounded-lg border bg-card pl-1"
                >
                  <span
                    data-attachment-thumb
                    className="relative flex size-9 shrink-0 items-center justify-center self-center overflow-hidden rounded-md bg-muted text-muted-foreground"
                  >
                    {isImage ? (
                      <img
                        alt={name}
                        src={attachment.preview || attachment.mediaUrl}
                        className="size-full object-cover"
                      />
                    ) : (
                      <>
                        <FileText className="size-4" aria-hidden />
                        <span className="sr-only">{name}</span>
                      </>
                    )}
                    {attachment.loading && (
                      <span className="absolute inset-0 flex items-center justify-center bg-background/60">
                        <Loader2 className="size-4 animate-spin text-foreground" aria-hidden />
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    onClick={() => this.removeComposerAttachment(attachment)}
                    className={removeClass}
                  >
                    {removeBadge}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        {this.renderStripCount()}
      </div>
    );
  };

  renderTemplates = () => {
    // fuse will set searched templates to state
    const { searchedTemplates } = this.state;

    // all templates are set in props by redux
    // IMPORTANT: never mutate redux state arrays (e.g. via push()).
    // Use a local copy and only show persisted templates from the API.
    const allTemplates = Array.isArray(this.props.templates) ? [...this.props.templates] : [];

    // Filter templates to show payment request template first when payment info is available
    let templates = [...allTemplates];

    if (this.state.showPaymentRequestCompleted) {
      // Make Payment Request template appear first when payment info is available
      templates = templates.map((template) => {
        if (template.title === 'Payment Request') {
          return {
            ...template,
            isFavorite: true,
            createdAt: new Date().toISOString(), // Make it the newest
          };
        }
        return template;
      });
    }

    // if the user searches for templates, show those. otherwise show all templates
    templates = searchedTemplates || templates;

    return (
      <TemplateSelector
        templates={templates}
        handleTemplateClick={this.handleTemplateClick}
        templatesExpanded={this.state.templatesExpanded}
        showEmojiPicker={this.state.showEmojiPicker}
        toggleTemplates={this.toggleTemplates}
        onOpenChange={(expanded) => this.setState({ templatesExpanded: expanded })}
        deleteTemplate={this.deleteTemplate}
        getUserTemplates={this.getUserTemplates}
        // the payment-request hoist pins the template to the top of "All" —
        // a remembered folder chip would hide it, so force the All view open
        filterOverride={this.state.showPaymentRequestCompleted ? 'all' : null}
        renderDialog
      />
    );
  };

  renderConvenienceFeeEstimate = () => {
    if (this.state.includeConvenienceFee) {
      const amount = Number(this.state.amount); // Ensure amount is a number
      const fee = Math.round(amount * 0.029 * 100) / 100; // Calculate and round the fee to two decimal places
      const total = Math.round((amount + fee) * 100) / 100; // Calculate and round the total to two decimal places

      return (
        <div className="text-xs text-muted-foreground">
          (2.9% fee: ${fee.toFixed(2)} Total: ${total.toFixed(2)})
        </div>
      );
    }

    return null;
  };

  renderPayments = () => {
    const isFromCalifornia = this.props.auth?.address?.includes('California');

    return (
      <div className="relative">
        <div
          className={cn(
            'transition-all transform origin-bottom z-0 duration-200 ease-in-out',
            // Sideways with the keyboard up, the form opens in place, so the
            // field the caret moves to is where it will stay when it's revealed.
            'kb-open:max-xl:[@media(max-height:560px)]:transition-none',
            this.state.paymentsExpanded
              ? 'translate-y-0 opacity-100 pointer-events-auto '
              : 'translate-y-full opacity-0 pointer-events-none h-0 overflow-hidden ease-out',
          )}
        >
          <Card className="my-2">
            <CardHeader className="flex flex-row gap-x-1 items-center pb-2">
              <CardTitle>Payment request</CardTitle>
              <IconButton
                className="m-0!"
                onClick={() =>
                  window.open(
                    'https://help.chekkit.io/article/77-how-do-i-send-a-payment-request',
                    '_blank',
                  )
                }
                aria-label="help"
              >
                <CircleHelp className="text-muted-foreground size-4" />
              </IconButton>
            </CardHeader>
            <CardContent className="flex flex-col gap-y-2 w-full pb-2">
              <div className="flex flex-row w-full gap-x-2">
                <div className="flex flex-col gap-y-2 w-1/2">
                  <div className="flex relative justify-end items-center">
                    <Input
                      placeholder="Amount"
                      id="amount"
                      onChange={this.onAmountChange}
                      value={this.state.amount}
                      // Only when the panel mounts open: collapsed, it would take the
                      // focus (and the next keystrokes) from the message box when a
                      // rotation swaps the phone composer for this one.
                      autoFocus={this.state.paymentsExpanded || Boolean(this.props.openPaymentsOnMount)}
                    />
                    <div className="absolute px-2">
                      <DollarSign className="text-muted-foreground size-4" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-y-2 w-1/2">
                  <div className="flex relative justify-end items-center">
                    <Input
                      id="reference"
                      placeholder="Reference"
                      onChange={this.onReferenceChange}
                      value={this.state.reference}
                    />
                    <div className="absolute px-2">
                      <Hash className="text-muted-foreground size-4" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="w-full space-y-2">
                <div className="flex relative justify-end items-center">
                  <Input
                    id="description"
                    onChange={this.onDescriptionChange}
                    value={this.state.description}
                    placeholder="Description"
                  />
                  <div className="absolute px-2">
                    <NotebookPen className="text-muted-foreground size-4" />
                  </div>
                </div>
              </div>

              {!isFromCalifornia ? (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="convenience-fee"
                    checked={this.state.includeConvenienceFee}
                    onCheckedChange={(e) => this.toggleConvenienceFee(e)}
                  />
                  <label
                    htmlFor="convenience-fee"
                    className="flex items-center text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    Include convenience fee{' '}
                    <IconButton
                      className="m-0!"
                      onClick={() =>
                        window.open(
                          'https://help.chekkit.io/article/98-adding-a-convenience-fee-to-your-chekkit-pay-payment-request',
                          '_blank',
                        )
                      }
                      aria-label="help"
                    >
                      <CircleHelp className="text-muted-foreground size-4" />
                    </IconButton>
                    {this.renderConvenienceFeeEstimate()}
                  </label>
                </div>
              ) : (
                <></>
              )}

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="add-template"
                  checked={this.state.addPaymentRequestTemplate}
                  onCheckedChange={(e) => this.toggleAddPaymentRequestTemplate(e)}
                />
                <label
                  htmlFor="add-template"
                  className="flex items-center text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Add Payment Request template
                </label>
              </div>
            </CardContent>
            <CardFooter className="flex pt-0">
              <div className="flex gap-x-1 w-full justify-end">
                <Button
                  className=""
                  variant="secondary"
                  aria-label="close"
                  onClick={this.togglePayments}
                >
                  Cancel
                </Button>
                <Button className="" aria-label="add" onClick={this.handlePaymentAccept}>
                  Add
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  };

  renderPaymentRequestCompleted = () => {
    let totalAmount = 0;

    if (this.state.includeConvenienceFee) {
      const amount = Number(this.state.amount); // Ensure amount is a number
      const fee = Math.round(amount * 0.029 * 100) / 100; // Calculate and round the fee to two decimal places
      totalAmount = Math.round((amount + fee) * 100) / 100; // Calculate and round the total to two decimal places
    } else {
      totalAmount = Number(this.state.amount);
    }

    if (this.state.showPaymentRequestCompleted) {
      return (
        <div className={cn('flex items-start gap-2 mb-2 w-fit', SIDEWAYS_HIDE)}>
          <Card className="w-[280px] bg-card/80 border-0 shadow-lg rounded-lg overflow-hidden backdrop-blur-sm">
            <div className="p-4">
              {/* Title */}
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-foreground">Payment Request Preview</h3>
              </div>

              {/* Payment details with icons */}
              <div className="space-y-2 mb-3">
                <div className="flex items-center space-x-2">
                  <CreditCard className="size-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">
                    {formatAmount({
                      amount: totalAmount * 100,
                    })}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <Hash className="size-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground">
                    {this.state.reference}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <NotebookPen className="size-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm font-medium text-foreground truncate">
                    {this.state.description}
                  </span>
                </div>
              </div>

              {/* Convenience fee disclosure */}
              {this.state.includeConvenienceFee && (
                <div className="bg-yellow-50 border border-orange-200 rounded-lg p-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-white border border-black rounded-full flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-2.5 h-2.5 text-black"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-orange-800">
                      Includes 2.9% convenience fee
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Action buttons positioned outside */}
          <div className="flex flex-col gap-1">
            <IconButton
              onClick={this.handlePaymentEdit}
              className="h-8 w-8 bg-background hover:bg-accent border border-border rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              size="sm"
            >
              <Edit className="size-4 text-muted-foreground" />
            </IconButton>
            <IconButton
              onClick={this.handlePaymentClose}
              className="h-8 w-8 bg-background hover:bg-accent border border-border rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
              size="sm"
            >
              <X className="size-4 text-muted-foreground" />
            </IconButton>
          </div>
        </div>
      );
    }

    return <div />;
  };

  // group context: composing to 2+ recipients, or viewing a group conversation.
  // Per-customer actions (payments, scheduling) aren't available in groups yet.
  isGroupContext = () => Boolean(this.props.isGroupMode || this.props.activeConversation?.isGroup);

  renderPaymentButton = () => {
    if (this.isGroupContext()) {
      return null;
    }

    const { accessLevel, isEnterpriseEnabled } = this.props.auth.user;

    if (!this.props.auth.products) {
      return;
    }

    // for enterprise only enable payment button on conversations with businesses who have payments enabled
    if (isEnterpriseEnabled) {
      const { activeConversation, businessGroup } = this.props;

      if (activeConversation?._business) {
        const currentBusiness = businessGroup.filter(
          (business) => business._id === activeConversation._business,
        )[0];

        if (!currentBusiness?.products?.includes('payments')) {
          return null;
        }

        return (
          <div>
            <IconButton
              aria-label="payments"
              onClick={this.togglePayments}
              disabled={this.state.loading}
              toolTipTitle="Create a new invoice"
            >
              <DollarSign className="size-4" />
            </IconButton>
          </div>
        );
      }

      return null;
    }

    if (
      this.props.auth.products?.includes('payments') &&
      !this.props.auth.stripeAccountIsConnected
    ) {
      // if they have payments and have not finished the onboarding
      // if they have not finished the onboarding disable button
      return (
        <div>
          <IconButton
            aria-label="payments"
            onClick={this.togglePayments}
            disabled
            toolTipTitle="Please finish setting up your stripe account on the
							payment settings page."
          >
            <DollarSign className="size-4" />
          </IconButton>
        </div>
      );
    }

    // if business has signed up for payments and
    // and they have the correct permission

    if (this.props.auth.products?.includes('payments') && PAYMENT_ACCESS_LEVELS.includes(accessLevel)) {
      return (
        <div>
          <IconButton
            aria-label="payments"
            onClick={this.togglePayments}
            disabled={this.state.loading}
            toolTipTitle="Attach payment request"
          >
            <DollarSign className="size-4" />
          </IconButton>
        </div>
      );
    }

    return <span />;
  };

  sendCustomerPortalLink = () => {
    // add ((customer_portal)) to the message, do nothing if already exists
    if (this.state.message.includes('((customer_portal))')) {
      return;
    }

    this.setState({
      message: `${this.state.message} ((customer_portal))`,
    });
  };

  openFileSelector = () => {
    // Note that the ref is set async,
    // so it might be null at some point
    if (this.dropzoneRef.current) {
      this.dropzoneRef.current.open();
    }
  };

  showDatePicker = () => {
    if (this.datePickerRef.current) {
      this.datePickerRef.current.showPicker();
    }
  };

  // Autoresize the textarea to fit its content
  autoResizeTextarea = (ref) => {
    if (ref) {
      requestAnimationFrame(() => {
        // Touch screens pin the thread to its newest message as the composer
        // grows. Hold the field's box while it is measured, or the brief
        // 'auto' collapse grows the thread and scrolls it off the bottom.
        const holder = isTouchViewport() ? ref.parentElement : null;
        if (holder) holder.style.minHeight = `${holder.offsetHeight}px`;
        ref.style.height = 'auto';
        ref.style.height = `${ref.scrollHeight}px`;
        if (holder) holder.style.minHeight = '';
      });
    }
  };

  renderComposerModeToggle = () => {
    const isNote = this.state.composerMode === 'note';
    const customerName = this.props.activeConversation?._customer?.name;
    const replyLabel = this.props.activeConversation?._customer
      ? `Reply to ${customerName || 'customer'}`
      : 'Reply';

    return (
      <div className={cn('flex items-center px-1 pt-1', SIDEWAYS_TYPING_HIDE, SIDEWAYS_FULL_ROW)}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'h-8 gap-1.5 px-2 text-[13px] font-normal',
                isNote ? 'text-yellow-700 dark:text-yellow-400' : 'text-muted-foreground',
              )}
            >
              {isNote ? (
                <NotebookPen className="size-3.5" />
              ) : (
                <MessageSquare className="size-3.5" />
              )}
              {isNote ? 'Note' : replyLabel}
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            <DropdownMenuItem
              onSelect={() => this.setComposerMode('reply')}
              className="justify-between"
            >
              <span className="flex items-center gap-2">
                <MessageSquare className="size-3.5" />
                Reply
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                {!isNote && <Check className="size-3.5" />}
                <kbd className="rounded border bg-muted px-1 text-[10px] leading-4">R</kbd>
              </span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => this.setComposerMode('note')}
              className="justify-between"
            >
              <span className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
                <NotebookPen className="size-3.5" />
                Note
              </span>
              <span className="flex items-center gap-1.5 text-muted-foreground">
                {isNote && <Check className="size-3.5" />}
                <kbd className="rounded border bg-muted px-1 text-[10px] leading-4">N</kbd>
              </span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  handleAttachmentDropRejected = (fileRejections) => {
    const errorCodes = new Set(
      fileRejections.flatMap((rejection) => rejection.errors.map((error) => error.code)),
    );

    let errorMessage = 'One or more files could not be attached.';
    if (errorCodes.has('too-many-files')) {
      errorMessage = TOO_MANY_ATTACHMENTS_MESSAGE;
    } else if (errorCodes.has('file-too-large')) {
      errorMessage = 'Attachments must be smaller than 20mb.';
    } else if (errorCodes.has('file-invalid-type')) {
      errorMessage = "That file type isn't supported.";
    }

    this.setState({
      errorMessage,
      errorTitle: 'Cannot attach file',
      openErrorDialog: true,
    });
  };

  renderInputBar = () => {
    const { loading } = this.state;
    const { resolvedTheme: theme } = this.context;
    const isNote = this.state.composerMode === 'note';

    const videoChatIconDisabled = this.videoChatIconDisabled();
    const aiDraft = this.getCurrentAiDraft();
    const isAiDraftLoaded = isDraftInComposer(
      aiDraft,
      this.state.activeAiDraftId,
      this.state.message,
    );
    const activeConversationId = this.props.activeConversation?._id;

    return (
      <Dropzone
        onDrop={this.addAttachments}
        onDropRejected={this.handleAttachmentDropRejected}
        ref={this.dropzoneRef}
        noClick
        noKeyboard
        maxFiles={MAX_ATTACHMENTS}
        maxSize={MAX_ATTACHMENT_BYTES}
        accept={attachmentAcceptConfig}
      >
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <div
              id="chatBoxTextBoxAndButtons"
              className={cn(
                'dropzone overflow-hidden rounded-2xl border border-foreground/30 transition-colors focus-within:border-foreground/60 focus-within:ring-1 focus-within:ring-ring/30',
                isNote
                  ? 'bg-yellow-50 dark:bg-yellow-950/40 border-yellow-300 dark:border-yellow-900/80 focus-within:ring-yellow-400'
                  : 'bg-background',
                isDragActive ? 'border-dashed border-primary' : '',
                this.hasSidewaysAttachments()
                  ? SIDEWAYS_ROW_WITH_ATTACHMENTS
                  : SIDEWAYS_TYPING_ROW,
              )}
              {...getRootProps()}
              onPasteCapture={isNote ? this.handleNotePaste : undefined}
            >
              {this.renderComposerModeToggle()}
              {hasAiEmployeeProduct(this.props.auth) && hasAiDraftContent(aiDraft) && (
                <div className={cn(SIDEWAYS_TYPING_HIDE, SIDEWAYS_FULL_ROW)}>
                  <AiDraftCard
                    key={this.getAiDraftId(aiDraft) || activeConversationId}
                    draft={aiDraft}
                    loaded={isAiDraftLoaded}
                    composerText={this.state.message}
                    onUse={() => this.applyAiDraft(aiDraft)}
                    onDismiss={this.dismissAiDraft}
                  />
                </div>
              )}
              {this.renderSidewaysAttachments()}
              {isNote ? <NoteEditor key={activeConversationId} embedded
                value={this.state.noteDraft?.text || ''} mentions={this.state.noteDraft?.mentions || []}
                _customer={this.props.activeConversation?._customer?._id} _business={this.props.activeConversation?._business}
                onChange={this.changeNoteDraft} onSubmit={this.sendNoteMessage} disabled={Boolean(this.state.savingNote)}
                autoFocus={Boolean(this.state.noteAutoFocus)}
                // A phone held sideways: Return is a new line (as in reply mode) and
                // the note stops growing before it pushes the thread off a short screen.
                submitOnEnter={!this.usesReturnForNewLine()}
                editorClassName="max-xl:[@media(max-height:560px)]:min-h-11 max-xl:[@media(max-height:560px)]:max-h-[calc(var(--app-height)*0.22)] kb-open:max-xl:[@media(max-height:560px)]:min-h-10"
              /> : (              <Textarea
                id="chatBoxTextBox"
                data-onboarding="inbox-composer"
                placeholder={
                  isNote ? 'Leave a note for your team...' : 'Type your message...'
                }
                className="min-h-20 resize-none border-0 bg-transparent px-3 py-2 text-[15px] leading-6 md:text-[15px] shadow-none focus-visible:ring-0 max-h-80 overflow-y-auto max-xl:[@media(max-height:560px)]:min-h-11 max-xl:[@media(max-height:560px)]:max-h-[calc(var(--app-height)*0.22)] kb-open:max-xl:[@media(max-height:560px)]:min-h-10"
                value={this.state.aiIsTyping ? this.state.aiTypingText : this.state.message}
                onChange={this.onTextChange}
                onFocus={this.handleTextFieldFocused}
                onBlur={this.handleTextFieldBlurred}
                maxLength={1600}
                readOnly={this.state.aiIsTyping}
                ref={this.setChatboxElement}
                // One row, so the autosize can shrink a one-line message to the
                // field's minimum on a short screen (the default is two rows).
                rows={1}
              />)}
              <div className={cn('flex flex-col gap-2 p-2 kb-open:max-xl:[@media(max-height:560px)]:p-1', SIDEWAYS_FULL_ROW)}>
                {/* Main toolbar row */}
                <div className="flex items-center justify-between gap-2">
                  {/* Left side - reply-only tools, hidden in Note mode */}
                  {isNote ? (
                    <div className={cn('flex flex-1 items-center gap-2 px-1', SIDEWAYS_TYPING_HIDE)}>
                      <IconButton
                        data-testid="templates-button-note"
                        aria-label="templates"
                        onClick={this.toggleTemplates}
                        disabled={loading}
                        toolTipTitle="Use template"
                        className="flex-shrink-0"
                      >
                        <BookOpen className="size-4" />
                        <span className="sr-only">Use template</span>
                      </IconButton>
                      <input {...getInputProps()} />
                      <IconButton
                        data-testid="attach-button-note"
                        aria-label="Attach photo or file"
                        onClick={this.openFileSelector}
                        disabled={loading || this.state.savingNote}
                        toolTipTitle="Attach photo or file"
                        className="flex-shrink-0"
                      >
                        <Paperclip className="size-4" />
                      </IconButton>
                      <span className="text-xs text-yellow-700/80 dark:text-yellow-500/80">
                        Internal note — only your team can see this
                      </span>
                    </div>
                  ) : (
                    <div className={cn('relative flex-1 min-w-0', SIDEWAYS_TYPING_HIDE)}>
                      <div
                        ref={this.setToolsScroller}
                        onScroll={this.updateToolsScroll}
                        className="overflow-x-auto scrollbar-none"
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          WebkitOverflowScrolling: 'touch',
                        }}
                      >
                        <style>{`
												.scrollbar-none::-webkit-scrollbar {
													display: none;
												}
											`}</style>
                        <div className="flex gap-x-1 items-center w-max pr-2">
                          {/* Primary actions */}
                          <IconButton
                            data-testid="templates-button"
                            aria-label="templates"
                            onClick={this.toggleTemplates}
                            disabled={loading}
                            toolTipTitle="Use template"
                            className="flex-shrink-0"
                          >
                            <BookOpen className="size-4" />
                            <span className="sr-only">Use template</span>
                          </IconButton>

                          <div className="flex flex-shrink-0">
                            <div>
                              <input {...getInputProps()} />

                              <IconButton
                                aria-label="Attach File"
                                disabled={loading}
                                onClick={this.openFileSelector}
                                toolTipTitle="Attach File"
                              >
                                <Paperclip className="size-4" />
                              </IconButton>
                            </div>
                          </div>

                          {/* Secondary actions */}
                          {this.renderPaymentButton()}

                          <AttachFormButton
                            disabled={loading}
                            onInsert={(code) =>
                              this.setState((prev) => ({
                                message: `${prev.message || ''} ((${code}))`,
                              }))
                            }
                          />

                          <AddToSequenceButton activeConversation={this.props.activeConversation} />

                          {!this.props.auth.reseller && (
                            <IconButton
                              aria-label="customer portal link"
                              onClick={this.sendCustomerPortalLink}
                              disabled={loading}
                              toolTipTitle="Send customer portal link"
                              className="flex-shrink-0"
                            >
                              <BriefcaseBusiness className="size-4" />
                              <span className="sr-only">Customer portal request form</span>
                            </IconButton>
                          )}

                          <AIEnhanceButton
                            generateAIResponse={this.props.generateAIResponse}
                            products={this.props.auth.products}
                            message={this.state.message}
                            updateMessageWithAI={this.updateMessageWithAI}
                            fetchUser={this.props.fetchUser}
                            activeConversation={this.props.activeConversation}
                            onTypingStateChange={this.handleAITypingStateChange}
                          />

                          <IconButton
                            onClick={this.insertVideoChatText}
                            disabled={videoChatIconDisabled}
                            toolTipTitle={
                              videoChatIconDisabled
                                ? 'Purchase Video Chat to unlock'
                                : 'Start Video Chat'
                            }
                            className="flex-shrink-0"
                          >
                            <Video className="size-4" />
                          </IconButton>
                        </div>
                      </div>
                      {this.renderToolsScrollButton(-1)}
                      {this.renderToolsScrollButton(1)}
                    </div>
                  )}

                  {/* Right side - send controls (always visible). Typing
                      sideways on a phone keeps only Send (or Add note). */}
                  <div className="flex gap-1 items-center flex-shrink-0 kb-open:max-xl:[@media(max-height:560px)]:[&>:not(:last-child)]:hidden">
                    <Separator orientation="vertical" className="mx-1 h-4" />

                    <TalkButton
                      value={
                        isNote
                          ? this.state.noteDraft?.text || ''
                          : this.state.message
                      }
                      maxLength={isNote ? 10000 : 1600}
                      disabled={
                        loading ||
                        this.state.aiIsTyping ||
                        this.state.savingNote
                      }
                      onChange={(next) => {
                        if (this.state.composerMode === 'note') {
                          this.changeNoteDraft(
                            next,
                            this.state.noteDraft?.mentions || []
                          );
                          return;
                        }
                        this.setState({ message: next });
                      }}
                    />

                    <Popover
                      open={this.state.showEmojiPicker}
                      onOpenChange={this.toggleEmojiPicker}
                    >
                      <PopoverTrigger asChild>
                        <IconButton>
                          <Smile className="size-4" />
                        </IconButton>
                      </PopoverTrigger>
                      <PopoverContent
                        align="end"
                        className="p-0 bg-transparent border-0 outline-0 m-0"
                      >
                        <Picker
                          onEmojiSelect={this.addEmoji}
                          data={data}
                          showPreview={false}
                          showSkinTones={false}
                          useButton={false}
                          theme={theme}
                        />
                      </PopoverContent>
                    </Popover>

                    {!isNote && (
                      <AlertDialog
                        open={Boolean(this.state.scheduleOpen) && this.state.scheduleOpen !== 'menu'}
                        onOpenChange={this.handleScheduleOpenChange('clock')}
                      >
                        <AlertDialogTrigger asChild>
                          <IconButton
                            onClick={this.toggleDatePicker}
                            disabled={
                              isSignupSendingBlocked(this.props.auth) ||
                              loading ||
                              this.isGroupContext() ||
                              (!this.state.message && !this.state.showPaymentRequestCompleted)
                            }
                            toolTipTitle={
                              this.isGroupContext()
                                ? 'Scheduling isn’t available in group chats yet'
                                : 'Schedule message'
                            }
                          >
                            <Clock className="size-4" />
                          </IconButton>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="font-sans">
                          <AlertDialogTitle>Schedule message</AlertDialogTitle>
                          {this.renderScheduleTimeFields('date-picker-dialog-inline')}
                          {this.renderSchedulePreview()}
                          <AlertDialogFooter className="flex w-full">
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction asChild>
                              <Button
                                type="submit"
                                onClick={this.confirmSchedule}
                                disabled={
                                  isSignupSendingBlocked(this.props.auth) ||
                                  this.state.loading ||
                                  new Date(this.state.scheduledSendDate) < new Date()
                                }
                              >
                                Schedule message
                              </Button>
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}

                    {isNote ? (
                      // Notes save optimistically (composer clears + a "Sending…"
                      // bubble appears immediately), so there is no loading
                      // spinner here — that feedback lives on the bubble.
                      <Button
                        size="sm"
                        onClick={this.sendNoteMessage}
                        disabled={loading || !this.canSaveNote()}
                        className="gap-1.5 bg-yellow-400 text-yellow-950 hover:bg-yellow-500 dark:bg-yellow-500 dark:text-yellow-950 dark:hover:bg-yellow-400"
                      >
                        <NotebookPen className="size-4" />
                        Add note
                      </Button>
                    ) : (
                      <div
                        className={cn(
                          'inline-flex items-center overflow-hidden ring-offset-background transition-colors justify-center rounded-xl',
                          {
                            'bg-primary hover:bg-primary/90':
                              this.state.message || this.state.showPaymentRequestCompleted,
                            'bg-muted':
                              !this.state.message && !this.state.showPaymentRequestCompleted,
                          },
                        )}
                      >
                        <Button
                          variant="default"
                          className={cn(
                            'bg-transparent shadow-none',
                            !this.state.message && !this.state.showPaymentRequestCompleted
                              ? 'text-foreground!'
                              : '',
                          )}
                          size="sm"
                          onClick={this.sendTextMessage}
                          data-onboarding="inbox-send"
                          aria-label="Send message"
                          disabled={
                            isSignupSendingBlocked(this.props.auth) ||
                            loading ||
                            (!this.state.message && !this.state.showPaymentRequestCompleted)
                          }
                        >
                          {loading ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Send className="size-4" />
                          )}
                        </Button>
                        <Separator orientation="vertical" className={cn('mx-1 h-4')} />
                        <AlertDialog
                          open={this.state.scheduleOpen === 'menu'}
                          onOpenChange={this.handleScheduleOpenChange('menu')}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="default"
                                className={cn(
                                  'bg-transparent shadow-none',
                                  !this.state.message && !this.state.showPaymentRequestCompleted
                                    ? 'text-foreground!'
                                    : '',
                                )}
                                size="sm"
                                disabled={
                                  isSignupSendingBlocked(this.props.auth) || loading ||
                                  (!this.state.message && !this.state.showPaymentRequestCompleted)
                                }
                              >
                                <ChevronDown className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() =>
                                  this.setState(
                                    {
                                      shouldCloseConversation: false,
                                    },
                                    () =>
                                      this.sendTextMessage({
                                        sendInChannel: 'phone',
                                      }),
                                  )
                                }
                                disabled={
                                  loading || !this.props.activeConversation?._customer?.phone
                                }
                              >
                                Send as SMS
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onSelect={() =>
                                  this.setState(
                                    {
                                      shouldCloseConversation: false,
                                    },
                                    () =>
                                      this.sendTextMessage({
                                        sendInChannel: 'email',
                                      }),
                                  )
                                }
                                disabled={
                                  loading || !this.props.activeConversation?._customer?.email
                                }
                              >
                                Send as email
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onSelect={() =>
                                  this.setState(
                                    {
                                      shouldCloseConversation: true,
                                    },
                                    () => this.sendTextMessage(),
                                  )
                                }
                                disabled={loading}
                              >
                                Send & close conversation
                              </DropdownMenuItem>
                              <AlertDialogTrigger asChild>
                                {this.renderDatePicker()}
                              </AlertDialogTrigger>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <AlertDialogContent className="font-sans">
                            <AlertDialogTitle>Schedule message</AlertDialogTitle>
                            {this.renderScheduleTimeFields('date-picker-dialog')}
                            {this.renderSchedulePreview()}
                            <AlertDialogFooter className="flex w-full">
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction asChild>
                                <Button
                                  type="submit"
                                  onClick={this.confirmSchedule}
                                  disabled={
                                    isSignupSendingBlocked(this.props.auth) ||
                                    this.state.loading ||
                                    new Date(this.state.scheduledSendDate) < new Date()
                                  }
                                >
                                  Schedule message
                                </Button>
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      </Dropzone>
    );
  };

  // ---- Phones ---------------------------------------------------------------
  // Below md the composer is a Messages-style bar: "+" opens every tool in one
  // sheet, the text sits in a pill that grows to a third of the screen, and a
  // round button sends. Return adds a new line. Long-pressing send offers the
  // send options, which the "+" sheet lists too.

  canSendFromPhone = () => {
    const { loading, message, attachments, showPaymentRequestCompleted, aiIsTyping } = this.state;
    if (isSignupSendingBlocked(this.props.auth) || loading || aiIsTyping) return false;
    if (this.state.composerMode === 'note') return this.canSaveNote();
    return Boolean(
      message || showPaymentRequestCompleted || attachments.some((attachment) => attachment?.mediaUrl),
    );
  };

  sendFromPhone = () => {
    if (!this.canSendFromPhone()) return;
    if (this.state.composerMode === 'note') {
      this.sendNoteMessage();
      return;
    }
    this.sendTextMessage();
  };

  sendFromPhoneWith = ({ shouldCloseConversation = false, sendInChannel } = {}) => {
    this.setState({ shouldCloseConversation }, () => this.sendTextMessage({ sendInChannel }));
  };

  // The payment request entry, mirroring renderPaymentButton; null when hidden.
  getPhonePaymentAction = () => {
    if (this.isGroupContext() || !this.props.auth.products) return null;
    const { accessLevel, isEnterpriseEnabled } = this.props.auth.user;
    if (isEnterpriseEnabled) {
      const { activeConversation, businessGroup } = this.props;
      if (!activeConversation?._business) return null;
      const currentBusiness = businessGroup.filter(
        (business) => business._id === activeConversation._business,
      )[0];
      if (!currentBusiness?.products?.includes('payments')) return null;
      return { disabled: this.state.loading };
    }
    if (this.props.auth.products.includes('payments') && !this.props.auth.stripeAccountIsConnected) {
      return { disabled: true, description: 'Finish setting up Stripe in Payments settings first' };
    }
    if (this.props.auth.products.includes('payments') && PAYMENT_ACCESS_LEVELS.includes(accessLevel)) {
      return { disabled: this.state.loading };
    }
    return null;
  };

  getPhoneScheduleState = () => {
    if (this.isGroupContext()) return { disabled: true, description: 'Not available in group chats yet' };
    if (isSignupSendingBlocked(this.props.auth) || this.state.loading) return { disabled: true };
    if (!this.state.message && !this.state.showPaymentRequestCompleted) {
      return { disabled: true, description: 'Type a message first' };
    }
    return { disabled: false };
  };

  openPhoneSchedule = () => {
    this.toggleDatePicker();
    this.setState({ scheduleOpen: 'clock' });
  };

  getPhoneSendItems = () => {
    const canSend = this.canSendFromPhone();
    const customer = this.props.activeConversation?._customer;
    const schedule = this.getPhoneScheduleState();
    return [
      {
        id: 'send-close',
        label: 'Send and close conversation',
        icon: CheckCircle,
        disabled: !canSend,
        onSelect: () => this.sendFromPhoneWith({ shouldCloseConversation: true }),
      },
      {
        id: 'send-sms',
        label: 'Send as SMS',
        icon: MessageSquare,
        disabled: !canSend || !customer?.phone,
        onSelect: () => this.sendFromPhoneWith({ sendInChannel: 'phone' }),
      },
      {
        id: 'send-email',
        label: 'Send as email',
        icon: Mail,
        disabled: !canSend || !customer?.email,
        onSelect: () => this.sendFromPhoneWith({ sendInChannel: 'email' }),
      },
      {
        id: 'schedule',
        label: 'Schedule message',
        icon: Clock,
        disabled: schedule.disabled,
        description: schedule.description,
        onSelect: this.openPhoneSchedule,
      },
    ];
  };

  renderPhoneTools = () => {
    const { loading, composerMode, message } = this.state;
    const isNote = composerMode === 'note';
    const payment = this.getPhonePaymentAction();
    const videoDisabled = this.videoChatIconDisabled();
    const products = this.props.auth.products;
    const hasAiText = products?.includes('ai-text');
    const canWriteNotes = Boolean(this.props.activeConversation?._id && this.props.activeConversation?._customer);

    const templateItem = {
      id: 'template',
      label: 'Template',
      icon: BookOpen,
      disabled: loading,
      onSelect: this.toggleTemplates,
    };
    const modeItem = isNote
      ? {
          id: 'reply',
          label: 'Reply to customer',
          icon: MessageSquare,
          onSelect: () => this.setComposerMode('reply'),
        }
      : {
          id: 'note',
          label: 'Internal note',
          description: 'Only your team sees it',
          icon: NotebookPen,
          onSelect: () => this.setComposerMode('note'),
        };

    const needsText = (item) => ({
      ...item,
      disabled: !message,
      description: message ? undefined : 'Type a message first',
    });

    const fileItem = {
      id: 'file',
      label: 'Photo or file',
      icon: Paperclip,
      disabled: loading || (isNote && Boolean(this.state.savingNote)),
      // Synchronous, inside the tap, so iOS lets the file picker open.
      onSelect: this.openFileSelector,
    };

    const sections = isNote
      ? [{ id: 'note', items: [fileItem, templateItem] }, ...(canWriteNotes ? [{ id: 'mode', items: [modeItem] }] : [])]
      : [
          {
            id: 'add',
            items: [
              fileItem,
              templateItem,
              {
                id: 'form',
                label: 'Form',
                icon: FileText,
                disabled: loading,
                onSelect: () => this.setState({ mobileFormsOpen: true }),
              },
              ...(payment
                ? [
                    {
                      id: 'payment',
                      label: 'Payment request',
                      icon: DollarSign,
                      disabled: payment.disabled,
                      description: payment.description,
                      onSelect: this.togglePayments,
                    },
                  ]
                : []),
              ...(!this.props.auth.reseller
                ? [
                    {
                      id: 'portal',
                      label: 'Customer portal link',
                      icon: BriefcaseBusiness,
                      disabled: loading,
                      onSelect: this.sendCustomerPortalLink,
                    },
                  ]
                : []),
              {
                id: 'video',
                label: 'Video chat link',
                icon: Video,
                disabled: videoDisabled,
                description: videoDisabled ? 'Add Video Chat to your plan to use it' : undefined,
                onSelect: this.insertVideoChatText,
              },
            ],
          },
          ...(products
            ? [
                {
                  id: 'ai',
                  items: [
                    hasAiText
                      ? { id: 'ai', label: 'Write with AI', icon: Zap, page: 'ai', disabled: loading }
                      : {
                          id: 'ai',
                          label: 'Write with AI',
                          icon: Zap,
                          onSelect: () => this.aiEnhanceRef.current?.openEnable(),
                        },
                  ],
                },
              ]
            : []),
          {
            id: 'send',
            items: [
              {
                id: 'sequence',
                label: 'Add to sequence',
                icon: Workflow,
                onSelect: () => this.setState({ mobileSequenceOpen: true }),
              },
              ...this.getPhoneSendItems(),
            ],
          },
          ...(canWriteNotes ? [{ id: 'mode', items: [modeItem] }] : []),
        ];

    const run = (kind) => () => this.aiEnhanceRef.current?.run(kind);
    const pages = [
      {
        id: 'ai',
        title: 'Write with AI',
        sections: [
          {
            id: 'generate',
            items: [{ id: 'generate', label: 'Write a reply', icon: Pencil, onSelect: run('generate') }],
          },
          {
            id: 'rewrite',
            title: 'Rewrite your message',
            items: [
              needsText({ id: 'expand', label: 'Expand', icon: MessageCircleMore, onSelect: run('expand') }),
              needsText({ id: 'shorten', label: 'Shorten', icon: Minimize2, onSelect: run('shorten') }),
              needsText({ id: 'friendly', label: 'Friendly tone', icon: Smile, onSelect: run('friendly') }),
              needsText({ id: 'formal', label: 'Formal tone', icon: Handshake, onSelect: run('formal') }),
              needsText({ id: 'spellcheck', label: 'Spell check', icon: SpellCheck, onSelect: run('spellcheck') }),
            ],
          },
        ],
      },
    ];

    return (
      <ActionSheet
        open={this.state.mobileToolsOpen}
        onOpenChange={(open) => this.setState({ mobileToolsOpen: open })}
        title={isNote ? 'Note tools' : 'Add to message'}
        hideTitle
        sections={sections}
        pages={pages}
      />
    );
  };

  renderPhonePaymentSheet = () => {
    const isFromCalifornia = this.props.auth?.address?.includes('California');
    const fieldClass = 'h-11 pl-9 text-base md:text-base';
    const iconClass = 'pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground';

    return (
      <BottomSheet
        open={this.state.paymentsExpanded}
        onOpenChange={(open) => {
          if (!open && this.state.paymentsExpanded) this.togglePayments();
        }}
        title="Payment request"
        headerLeft={<BottomSheetTextButton onClick={this.togglePayments}>Cancel</BottomSheetTextButton>}
        headerRight={
          <BottomSheetTextButton strong onClick={this.handlePaymentAccept}>
            Add
          </BottomSheetTextButton>
        }
      >
        <form
          className="space-y-3 pb-1"
          onSubmit={(event) => {
            event.preventDefault();
            this.handlePaymentAccept();
          }}
        >
          <label className="block" htmlFor="amount-phone">
            <span className="mb-1 block text-[13px] font-medium text-muted-foreground">Amount</span>
            <span className="relative block">
              <DollarSign className={iconClass} aria-hidden />
              <Input
                id="amount-phone"
                inputMode="decimal"
                autoComplete="off"
                enterKeyHint="next"
                placeholder="0.00"
                className={fieldClass}
                onChange={this.onAmountChange}
                value={this.state.amount}
              />
            </span>
          </label>
          <label className="block" htmlFor="reference-phone">
            <span className="mb-1 block text-[13px] font-medium text-muted-foreground">Reference</span>
            <span className="relative block">
              <Hash className={iconClass} aria-hidden />
              <Input
                id="reference-phone"
                autoComplete="off"
                autoCapitalize="characters"
                enterKeyHint="next"
                placeholder="Invoice or job number"
                className={fieldClass}
                onChange={this.onReferenceChange}
                value={this.state.reference}
              />
            </span>
          </label>
          <label className="block" htmlFor="description-phone">
            <span className="mb-1 block text-[13px] font-medium text-muted-foreground">Description</span>
            <span className="relative block">
              <NotebookPen className={iconClass} aria-hidden />
              <Input
                id="description-phone"
                autoComplete="off"
                enterKeyHint="done"
                placeholder="What it's for"
                className={fieldClass}
                onChange={this.onDescriptionChange}
                value={this.state.description}
              />
            </span>
          </label>
          {!isFromCalifornia && (
            <label htmlFor="convenience-fee-phone" className="flex min-h-11 items-center gap-3 text-[15px]">
              <Checkbox
                id="convenience-fee-phone"
                checked={this.state.includeConvenienceFee}
                onCheckedChange={(checked) => this.toggleConvenienceFee(checked)}
                className="size-5"
              />
              <span className="min-w-0 flex-1">
                Include convenience fee
                {this.renderConvenienceFeeEstimate()}
              </span>
            </label>
          )}
          <label htmlFor="add-template-phone" className="flex min-h-11 items-center gap-3 text-[15px]">
            <Checkbox
              id="add-template-phone"
              checked={this.state.addPaymentRequestTemplate}
              onCheckedChange={(checked) => this.toggleAddPaymentRequestTemplate(checked)}
              className="size-5"
            />
            Add Payment Request template
          </label>
          <a
            href="https://help.chekkit.io/article/77-how-do-i-send-a-payment-request"
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center gap-1.5 text-[15px] text-primary"
          >
            <CircleHelp className="size-4" aria-hidden />
            How payment requests work
          </a>
          <button type="submit" className="sr-only" tabIndex={-1}>
            Add payment request
          </button>
        </form>
      </BottomSheet>
    );
  };

  renderPhoneScheduleSheet = () => (
    <BottomSheet
      open={Boolean(this.state.scheduleOpen)}
      onOpenChange={(open) => {
        if (!open) this.cancelSchedule();
      }}
      title="Schedule message"
      headerLeft={<BottomSheetTextButton onClick={this.cancelSchedule}>Cancel</BottomSheetTextButton>}
      headerRight={
        <BottomSheetTextButton
          strong
          onClick={this.confirmSchedule}
          disabled={
            isSignupSendingBlocked(this.props.auth) ||
            this.state.loading ||
            !this.state.scheduledSendDate ||
            new Date(this.state.scheduledSendDate) < new Date()
          }
        >
          Schedule
        </BottomSheetTextButton>
      }
    >
      <div className="space-y-3 pb-1">
        {this.renderScheduleTimeFields('date-picker-phone')}
        {this.renderSchedulePreview()}
      </div>
    </BottomSheet>
  );

  renderPhonePaymentPreview = () => {
    if (!this.state.showPaymentRequestCompleted) return null;
    const total = this.getPaymentRequestTotal();
    const details = [
      this.state.reference && `#${this.state.reference}`,
      this.state.description,
      this.state.includeConvenienceFee && 'includes 2.9% fee',
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <div className="mb-1.5 flex items-center gap-2 rounded-xl border bg-card py-0.5 pl-3 text-card-foreground">
        <CreditCard className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">
            Payment request · {formatAmount({ amount: total * 100 })}
          </p>
          {details && <p className="truncate text-xs text-muted-foreground">{details}</p>}
        </div>
        <button
          type="button"
          aria-label="Edit payment request"
          onClick={this.handlePaymentEdit}
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground active:bg-accent"
        >
          <Edit className="size-4" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Remove payment request"
          onClick={this.handlePaymentClose}
          className="flex size-11 shrink-0 items-center justify-center rounded-full text-muted-foreground active:bg-accent"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
    );
  };

  renderPhoneComposer = () => {
    const { loading } = this.state;
    const attachments = this.getComposerAttachments();
    const isNote = this.state.composerMode === 'note';
    const aiDraft = this.getCurrentAiDraft();
    const activeConversationId = this.props.activeConversation?._id;
    const canSend = this.canSendFromPhone();
    const messageLength = Number(this.state.message?.length || 0);

    return (
      <div className="relative z-10 flex shrink-0 flex-col border-t border-border/60 bg-background px-2 pt-1.5 pb-[max(0.375rem,var(--safe-bottom))] kb-open:pb-1.5">
        {isSignupSendingBlocked(this.props.auth) && (
          <p className="signup-sending-notice px-1 pb-1.5 text-xs">
            {this.props.auth?.selfServeSignup?.environment === 'staging'
              ? 'Staging test mode: you can write a message, but customer messages are not sent.'
              : 'You can write a message now. Sending unlocks after your number is approved and ready and your first payment succeeds.'}
          </p>
        )}
        <TooltipProvider>
          {this.renderErrorDialog()}
          {this.renderFacebookMessageTagDialog()}
          {loading ? null : this.renderTemplates()}

          {isNote && (
            <div className="mb-1.5 flex min-h-11 items-center gap-2 rounded-xl bg-yellow-100/80 pl-3 text-[13px] text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300">
              <NotebookPen className="size-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1 truncate">Internal note · only your team sees it</span>
              <button
                type="button"
                onClick={() => this.setComposerMode('reply')}
                className="h-11 shrink-0 rounded-xl px-3 font-medium active:bg-yellow-200/60 dark:active:bg-yellow-900/40"
              >
                Reply instead
              </button>
            </div>
          )}
          {hasAiEmployeeProduct(this.props.auth) && hasAiDraftContent(aiDraft) && (
            <div className="-mx-2 mb-1">
              <AiDraftCard
                key={this.getAiDraftId(aiDraft) || activeConversationId}
                draft={aiDraft}
                loaded={isDraftInComposer(aiDraft, this.state.activeAiDraftId, this.state.message)}
                composerText={this.state.message}
                onUse={() => this.applyAiDraft(aiDraft)}
                onDismiss={this.dismissAiDraft}
              />
            </div>
          )}
          {attachments.length > 0 && (
            <div className="scroll-touch -mx-2 mb-1.5 flex gap-2 overflow-x-auto px-2 pb-0.5 [&>*]:w-56 [&>*]:shrink-0">
              {attachments.map((attachment) => (
                <ImagePreview
                  key={attachment.path}
                  attachment={attachment}
                  removeAttachment={this.removeComposerAttachment}
                />
              ))}
            </div>
          )}
          {loading ? null : this.renderPhonePaymentPreview()}

          <Dropzone
            onDrop={this.addAttachments}
            onDropRejected={this.handleAttachmentDropRejected}
            ref={this.dropzoneRef}
            noClick
            noKeyboard
            maxFiles={MAX_ATTACHMENTS}
            maxSize={MAX_ATTACHMENT_BYTES}
            accept={attachmentAcceptConfig}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                id="chatBoxTextBoxAndButtons"
                className={cn('flex items-end gap-1', isDragActive && 'rounded-[22px] ring-2 ring-primary')}
                {...getRootProps()}
                onPasteCapture={isNote ? this.handleNotePaste : undefined}
              >
                <input {...getInputProps()} />
                <button
                  type="button"
                  aria-label={isNote ? 'Note tools' : 'Add a photo, template or other tool'}
                  aria-haspopup="dialog"
                  onClick={() => this.setState({ mobileToolsOpen: true })}
                  className="flex size-11 shrink-0 items-center justify-center rounded-full active:opacity-60"
                >
                  <span className="flex size-8 items-center justify-center rounded-full bg-muted text-foreground">
                    <Plus className="size-5" aria-hidden />
                  </span>
                </button>
                <div
                  className={cn(
                    'flex min-h-11 min-w-0 flex-1 items-end gap-1 rounded-[22px] border pl-3.5 pr-1 transition-colors',
                    isNote
                      ? 'border-yellow-300 bg-yellow-50 focus-within:border-yellow-400 dark:border-yellow-900/80 dark:bg-yellow-950/40'
                      : 'border-input bg-background focus-within:border-foreground/40',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    {isNote ? (
                      <NoteEditor
                        key={activeConversationId}
                        embedded
                        compact
                        submitOnEnter={false}
                        autoFocus={Boolean(this.state.noteAutoFocus)}
                        placeholder="Note for your team. Type @ to tag"
                        value={this.state.noteDraft?.text || ''}
                        mentions={this.state.noteDraft?.mentions || []}
                        _customer={this.props.activeConversation?._customer?._id}
                        _business={this.props.activeConversation?._business}
                        onChange={this.changeNoteDraft}
                        onSubmit={this.sendNoteMessage}
                        disabled={Boolean(this.state.savingNote)}
                      />
                    ) : (
                      <Textarea
                        id="chatBoxTextBox"
                        data-onboarding="inbox-composer"
                        aria-label="Message"
                        placeholder={this.isGroupContext() ? 'Message the group' : 'Message'}
                        rows={1}
                        className="block max-h-[min(9rem,calc(var(--app-height)*0.3))] min-h-[42px] resize-none overflow-y-auto border-0 bg-transparent px-0 py-[9px] text-base leading-6 shadow-none focus-visible:ring-0 md:text-base"
                        value={this.state.aiIsTyping ? this.state.aiTypingText : this.state.message}
                        onChange={this.onTextChange}
                        onFocus={this.handleTextFieldFocused}
                        onBlur={this.handleTextFieldBlurred}
                        maxLength={1600}
                        readOnly={this.state.aiIsTyping}
                        ref={this.setChatboxElement}
                        autoComplete="off"
                        autoCapitalize="sentences"
                        enterKeyHint="enter"
                        spellCheck
                      />
                    )}
                  </div>
                  <LongPressArea
                    disabled={isNote || !canSend}
                    onLongPress={() => this.setState({ mobileSendOptionsOpen: true })}
                    className="mb-1 shrink-0 rounded-full"
                  >
                    <button
                      type="button"
                      data-onboarding="inbox-send"
                      aria-label={isNote ? 'Add note' : 'Send message'}
                      disabled={!canSend}
                      // Keeps the text field focused, so the keyboard stays up after sending.
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={this.sendFromPhone}
                      className={cn(
                        'relative flex size-9 items-center justify-center rounded-full transition-colors before:absolute before:-inset-1',
                        canSend
                          ? isNote
                            ? 'bg-yellow-400 text-yellow-950'
                            : 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {loading || this.state.savingNote ? (
                        <Loader2 className="size-[18px] animate-spin" aria-hidden />
                      ) : isNote ? (
                        <Check className="size-5" strokeWidth={2.5} aria-hidden />
                      ) : (
                        <ArrowUp className="size-5" strokeWidth={2.5} aria-hidden />
                      )}
                    </button>
                  </LongPressArea>
                </div>
              </div>
            )}
          </Dropzone>
          {!isNote && messageLength > 1400 && (
            <p className="px-2 pt-1 text-right text-[11px] text-muted-foreground" aria-live="polite">
              {messageLength.toLocaleString()} / {(1600).toLocaleString()} characters
            </p>
          )}

          {this.renderPhoneTools()}
          <ActionSheet
            open={this.state.mobileSendOptionsOpen}
            onOpenChange={(open) => this.setState({ mobileSendOptionsOpen: open })}
            title="Send options"
            items={this.getPhoneSendItems()}
          />
          {this.renderPhonePaymentSheet()}
          {this.renderPhoneScheduleSheet()}
          <AIEnhanceButton
            ref={this.aiEnhanceRef}
            hideTrigger
            generateAIResponse={this.props.generateAIResponse}
            products={this.props.auth.products}
            message={this.state.message}
            updateMessageWithAI={this.updateMessageWithAI}
            fetchUser={this.props.fetchUser}
            activeConversation={this.props.activeConversation}
            onTypingStateChange={this.handleAITypingStateChange}
          />
          <AttachFormButton
            variant="sheet"
            open={this.state.mobileFormsOpen}
            onOpenChange={(open) => this.setState({ mobileFormsOpen: open })}
            disabled={loading}
            onInsert={(code) =>
              this.setState((prev) => ({
                message: `${prev.message || ''} ((${code}))`,
              }))
            }
          />
          <AddToSequenceButton
            variant="sheet"
            open={this.state.mobileSequenceOpen}
            onOpenChange={(open) => this.setState({ mobileSequenceOpen: open })}
            activeConversation={this.props.activeConversation}
          />
        </TooltipProvider>
      </div>
    );
  };

  render() {
    const { loading } = this.state;

    // short code senders (stored as +0...) are receive-only — carriers don't
    // allow replies to short codes from regular phone numbers
    if (this.props.activeConversation?._customer?.phone?.startsWith('+0')) {
      return (
        <div className="flex flex-col p-2 pt-0 max-md:pb-[max(0.5rem,var(--safe-bottom))]">
          <div className="rounded-md border bg-muted p-3 text-center text-sm text-muted-foreground">
            Short code — replies aren&apos;t supported
          </div>
        </div>
      );
    }

    if (this.props.isMobile) {
      return this.renderPhoneComposer();
    }

    return (
      <div
        className={cn(
          'relative z-10 flex shrink-0 flex-col bg-background px-3 pb-2 pt-2 sm:px-4 sm:pb-3 kb-open:max-xl:[@media(max-height:560px)]:py-1',
          SIDEWAYS_SCROLL,
        )}
      >
        {isSignupSendingBlocked(this.props.auth) && <p className="signup-sending-notice">{this.props.auth?.selfServeSignup?.environment === 'staging' ? 'Staging test mode: you can write a message, but customer messages are not sent.' : 'You can write a message now. Sending unlocks after your number is approved and ready and your first payment succeeds.'}</p>}
        <Dialog>
          <TooltipProvider>
            {this.renderErrorDialog()}
            {this.renderFacebookMessageTagDialog()}
            {loading ? null : this.renderTemplates()}
            {loading ? null : this.renderPayments()}
            {loading ? null : this.renderPaymentRequestCompleted()}

            {this.renderImagePreviews()}
            {this.renderInputBar()}
            <p className="w-full flex justify-end text-[11px] text-muted-foreground px-1 pt-1.5 max-xl:[@media(max-height:560px)]:hidden">
              {Number(this.state.message?.length || 0)?.toLocaleString()} /{' '}
              {(1600)?.toLocaleString()} characters
            </p>
          </TooltipProvider>
        </Dialog>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    auth: state.auth,
    businessGroup: state.businessGroup,
    timezone: state.auth.timezone,
    templates: state.templates,
    aiDraftByConversation: state.aiEmployee.draftByConversation,
    pendingComposerRestore: state.webchat.pendingComposerRestore,
  };
}

export default connect(mapStateToProps, {
  fetchUser,
  fetchCustomerRecentInvoices,
  fetchRecentInvoices,
  getTemplates,
  clearAiDraft,
  addOptimisticMessage,
  removeOptimisticMessage,
  clearComposerRestore,
})(ChatBox);
